import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { db, app } from "../firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "./SendInvoices.css";
import { addJobTotal } from "../utils/addJobTotal";
import JobHistoryPopup from "../components/JobHistoryPopup";
import JobCompletionInvoiceModal from "../components/JobCompletionInvoiceModal";
import ContinueJobPopup from "../components/ContinueJobPopup";
import { createContinuationJob, getNextContinuedWO } from "../utils/continueJobUtils";
import {
  expandCompletedOccurrences,
  isRecurring,
  toDateKey,
  getCompletionOccurrenceKey,
  getInvoicedDateSet,
  getCanonicalKeyForScheduledKey,
  getOverrideScheduledKey,
} from "../utils/recurringOccurrenceUtils";
import { saveRecurringJobEdit, closeOrDeleteJob } from "../utils/recurringEditUtils";
import { filterStaffForDropdowns } from "../utils/staffSettingsUtils";
import {
  getMaterialsGroupId,
  loadMaterialsForJob,
  saveMaterialsForJob,
} from "../utils/materialsUtils";
import { useServiceTypes, withCurrentValue } from "../utils/serviceTypeOptions";

/* ===========================================================================
   SEND INVOICES (Priority Pest Defense)

   Pipeline:
     Home / job tile: tech marks a job complete
        -> it shows up here in "Send Invoices" (one-time: status "Completed";
           recurring: one row per completed occurrence not yet invoiced)
        -> "Create Invoice" opens the Complete Job & Create Invoice form
           (JobCompletionInvoiceModal). Submitting writes an approvedInvoices
           doc (Approved or Draft). Nothing is emailed from here.
        -> the invoice is reviewed / edited / emailed on Approved Invoices.
           Emailing it there moves the job (or the occurrence) to
           "Sent Invoices" below.
     "Invoice Sent" here is the manual override for an invoice that went out
     some other way.

   Recurring jobs never get status "Invoice Sent" written on the master doc -
   that would close the whole series. Each occurrence is tracked in
   invoicedDates, keyed by the CANONICAL occurrence date
   (getCompletionOccurrenceKey), the same key completion uses.
   =========================================================================== */

// ---------------------------------------------------------------------------
// Which Firestore collection a loaded row came from. The list merges `jobs`
// and `addItem`; every write must go back to the right one.
// ---------------------------------------------------------------------------
const getJobCollectionName = (job) =>
  job?.__collection === "addItem" ? "addItem" : "jobs";

const todayKey = () => moment().format("YYYY-MM-DD");

/** Both keys an occurrence row can be recorded under (canonical first). */
const rowOccurrenceKeys = (row) =>
  Array.from(
    new Set(
      [toDateKey(row?.originalOccurrenceDate), toDateKey(row?.occurrenceDate)].filter(
        Boolean
      )
    )
  );

/**
 * The mirror of expandCompletedOccurrences for the "Sent Invoices" list:
 * one-time jobs whose status is "Invoice Sent", plus one row per invoiced
 * occurrence of a recurring job.
 */
const expandInvoicedOccurrences = (job) => {
  if (!isRecurring(job)) {
    return job?.status === "Invoice Sent" ? [job] : [];
  }

  const canonicalKeys = new Set();
  getInvoicedDateSet(job).forEach((dateKey) => {
    canonicalKeys.add(getCanonicalKeyForScheduledKey(job, dateKey) || dateKey);
  });

  return Array.from(canonicalKeys)
    .map((canonicalKey) => {
      const scheduledKey = getOverrideScheduledKey(job, canonicalKey) || canonicalKey;
      return {
        ...job,
        startDate: scheduledKey,
        endDate: scheduledKey,
        occurrenceDate: scheduledKey,
        originalOccurrenceDate: canonicalKey,
        status: "Invoice Sent",
        completedAt:
          job?.completedAtByDate?.[canonicalKey] ||
          job?.completedAtByDate?.[scheduledKey] ||
          job?.completedAt ||
          null,
        __rowKey: `${job.id}__${canonicalKey}`,
        __occurrenceRow: true,
      };
    })
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
};

// ---------------------------------------------------------------------------
// approvedInvoices <-> job rows
// ---------------------------------------------------------------------------
const invoiceJobId = (invoice) =>
  invoice?.jobId || invoice?.sourceJobId || invoice?.originalJobId || "";

const invoiceOccurrenceKeys = (invoice) =>
  [
    invoice?.occurrenceKey,
    invoice?.originalOccurrenceDate,
    invoice?.occurrenceDate,
    invoice?.dateOfService,
    invoice?.serviceDate,
  ]
    .map(toDateKey)
    .filter(Boolean);

const invoiceTime = (invoice) => {
  const value = invoice?.createdAt || invoice?.updatedAt;
  if (!value) return 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime();
  if (value?.seconds) return value.seconds * 1000;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/** Index approvedInvoices by the job they belong to (and by id). */
const indexInvoices = (invoices = []) => {
  const byJob = new Map();
  const byId = new Map();
  invoices.forEach((invoice) => {
    byId.set(invoice.id, invoice);
    const jobId = invoiceJobId(invoice);
    if (!jobId) return;
    if (!byJob.has(jobId)) byJob.set(jobId, []);
    byJob.get(jobId).push(invoice);
  });
  return { byJob, byId };
};

/** Every approvedInvoices doc created for THIS row (this occurrence if recurring). */
const getRowInvoices = (row, index) => {
  if (!row?.id || !index) return [];

  const own = [...(index.byJob.get(row.id) || [])];
  if (!isRecurring(row) && row.approvedInvoiceId) {
    const linked = index.byId.get(row.approvedInvoiceId);
    if (linked && !own.includes(linked)) own.push(linked);
  }

  if (!isRecurring(row)) {
    return own.sort((a, b) => invoiceTime(b) - invoiceTime(a));
  }

  const keys = new Set(rowOccurrenceKeys(row));
  return own
    .filter((invoice) => invoiceOccurrenceKeys(invoice).some((key) => keys.has(key)))
    .sort((a, b) => invoiceTime(b) - invoiceTime(a));
};

const invoiceStatusLabel = (invoice) => {
  const status = String(invoice?.status || "Draft").trim();
  if (status.toLowerCase() === "sent") return "Sent";
  return status || "Draft";
};

const statusTone = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "approved" || s === "reviewed") return "green";
  if (s === "draft") return "amber";
  if (s === "sent" || s === "invoice sent") return "blue";
  if (s === "paid") return "teal";
  if (s === "completed") return "green";
  if (s === "closed") return "grey";
  return "grey";
};

// Jobs may store completedAt as a Firestore Timestamp, an ISO string, or not
// at all (jobs completed before the field existed).
const formatCompletedAt = (completedAt) => {
  if (!completedAt) return null;
  const date =
    typeof completedAt?.toDate === "function" ? completedAt.toDate() : new Date(completedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const normalizeTechAssign = (value, keepEmpty = false) => {
  if (Array.isArray(value)) return keepEmpty ? value : value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const displayTechAssign = (value) => normalizeTechAssign(value).join(", ");

const parseFormattedJobDescription = (description) => {
  if (!description) return [];

  return String(description)
    .split("\n")
    .map((line) => line.trimEnd())
    .filter((line, index, arr) => {
      if (line !== "") return true;
      return arr[index - 1] !== "" || arr[index + 1] !== "";
    })
    .map((line, index) => {
      if (line.trim() === "") return <div key={index} style={{ height: "10px" }} />;
      if (line.startsWith("((b))")) {
        return (
          <div key={index} style={{ fontWeight: 700, marginBottom: 6 }}>
            {line.replace("((b))", "").trim()}
          </div>
        );
      }
      if (line.startsWith("((i))")) {
        return (
          <div key={index} style={{ fontStyle: "italic", marginBottom: 6 }}>
            {line.replace("((i))", "").trim()}
          </div>
        );
      }
      if (line.startsWith("((-))")) {
        return (
          <div key={index} style={{ marginLeft: 16, marginBottom: 6 }}>
            • {line.replace("((-))", "").trim()}
          </div>
        );
      }
      return (
        <div key={index} style={{ marginBottom: 6, lineHeight: 1.5 }}>
          {line}
        </div>
      );
    });
};

const getShortJobDescription = (description, maxLength = 140) => {
  if (!description) return "";
  const cleaned = String(description)
    .replace(/\(\(b\)\)/g, "")
    .replace(/\(\(i\)\)/g, "")
    .replace(/\(\(-\)\)/g, "• ")
    .replace(/\s+/g, " ")
    .trim();
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength)}...` : cleaned;
};

const isImageUrl = (url = "") => /\.(jpe?g|png|gif|webp)(\?|$)/i.test(String(url));

const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes si-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "si-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

/* ---------------------------------------------------------------------------
   Popup shell. Portaled to <body> with self-contained inline positioning so
   the app's competing .modal / .modal-overlay rules can never move it.
   layer "main"  -> 999998 / 999999  (popups opened from the page)
   layer "top"   -> 1999998 / 1999999 (popups opened on top of another popup)
--------------------------------------------------------------------------- */
const SiPopup = ({ title, subtitle, onClose, width = 560, layer = "top", children }) => {
  const base = layer === "main" ? 999998 : 1999998;

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.55)",
          zIndex: base,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="si-popup"
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          margin: 0,
          width: `min(${width}px, calc(100vw - 24px))`,
          maxHeight: "88vh",
          overflowY: "auto",
          background: "#ffffff",
          color: "#0f172a",
          padding: 20,
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          textAlign: "left",
          boxSizing: "border-box",
          zIndex: base + 1,
        }}
      >
        {(title || onClose) && (
          <div className="si-popup-head">
            <div>
              {title && <h3 className="si-popup-title">{title}</h3>}
              {subtitle && <p className="si-popup-sub">{subtitle}</p>}
            </div>
            {onClose && (
              <button type="button" className="si-popup-x" onClick={onClose} aria-label="Close">
                ×
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </>,
    document.body
  );
};

const SortButton = ({ columnKey, sortConfig, onSort }) => (
  <button type="button" className="si-sort" onClick={() => onSort(columnKey)}>
    {sortConfig.key === columnKey ? (sortConfig.direction === "ascending" ? "↑" : "↓") : "↕"}
  </button>
);

const Pill = ({ tone = "grey", children }) => (
  <span className={`si-pill si-pill--${tone}`}>{children}</span>
);

const companyOptions = ["PPD"];
const statusOptions = ["Active", "Completed", "Invoice Sent"];
const monthlyStatusOptions = ["Monthly", "Active", "Completed", "Invoice Sent", "Closed"];
const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i);

const EMPTY_MONTHLY_JOB = {
  siteName: "",
  management: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  wo: "",
  workOrder: "",
  scope: "",
  techAssign: "",
  companyUse: "PPD",
  status: "Monthly",
  price: "",
  frequency: "",
};

const SendInvoices = () => {
  const { serviceTypes } = useServiceTypes();
  const navigate = useNavigate();
  const auth = getAuth();

  const functions = getFunctions(app, "us-central1");
  const sendJobNoteEmailCallable = httpsCallable(functions, "sendJobNoteEmail");

  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [approvedJobs, setApprovedJobs] = useState([]);
  const [approvedInvoices, setApprovedInvoices] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [contractorsList, setContractorsList] = useState([]);
  const [monthlyJobs, setMonthlyJobs] = useState([]);
  const [jobTotals, setJobTotals] = useState({});
  const [userFullName, setUserFullName] = useState("");

  // Honors the Show In Dropdowns flag from the Staff Settings page.
  const dropdownUsers = useMemo(() => filterStaffForDropdowns(usersList), [usersList]);

  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedInvoiceJob, setSelectedInvoiceJob] = useState(null);
  const [selectedDepositJob, setSelectedDepositJob] = useState(null);
  const [selectedMonthlyJob, setSelectedMonthlyJob] = useState(null);

  const [invoiceModalJob, setInvoiceModalJob] = useState(null);

  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [alsoMarkInvoiceSent, setAlsoMarkInvoiceSent] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState(null);
  const [editingMode, setEditingMode] = useState(null); // "job" | "monthly"
  const [savingEdit, setSavingEdit] = useState(false);

  const [selectedJobIds, setSelectedJobIds] = useState([]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null); // 'status' | 'delete'
  const [bulkStatusTarget, setBulkStatusTarget] = useState(null); // 'Invoice Sent' | 'Active'
  const [bulkWorking, setBulkWorking] = useState(false);

  const [jobToUnapprove, setJobToUnapprove] = useState(null);
  const [jobToUnsend, setJobToUnsend] = useState(null);

  const [showClosedAccounts, setShowClosedAccounts] = useState(false);
  const [showSentInvoices, setShowSentInvoices] = useState(true);
  const [sendInvoicesSearch, setSendInvoicesSearch] = useState("");
  const [depositInvoicesSearch, setDepositInvoicesSearch] = useState("");
  const [monthlyInvoicesSearch, setMonthlyInvoicesSearch] = useState("");

  const [showAddMonthlyJobPopup, setShowAddMonthlyJobPopup] = useState(false);
  const [newMonthlyJob, setNewMonthlyJob] = useState(EMPTY_MONTHLY_JOB);
  const [showMonthlyInvoicePopup, setShowMonthlyInvoicePopup] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({
    invoiceMonth: "",
    dateInvoiceSent: "",
    paymentNumber: "",
  });

  const [showDeleteVerificationPopup, setShowDeleteVerificationPopup] = useState(false);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteTarget, setNoteTarget] = useState(null); // "job" | "monthly"
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [showDepositPricePopup, setShowDepositPricePopup] = useState(false);
  const [depositPrice, setDepositPrice] = useState("");
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);

  const [showTotalPopup, setShowTotalPopup] = useState(false);
  const [totalInput, setTotalInput] = useState("");

  const [showReceiptPopup, setShowReceiptPopup] = useState(false);
  const [receiptAmount, setReceiptAmount] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptNote, setReceiptNote] = useState("");
  const [savingReceipt, setSavingReceipt] = useState(false);

  const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
  const [materialsDraft, setMaterialsDraft] = useState([]);
  const [jobMaterials, setJobMaterials] = useState({});

  /* Continue: a new Active job stemming off this job's work order. The job it
     comes from is not completed and not modified. */
  const [continueSourceJob, setContinueSourceJob] = useState(null);
  const [continueNextWO, setContinueNextWO] = useState("");
  const [continueLoading, setContinueLoading] = useState(false);
  const [continueSaving, setContinueSaving] = useState(false);
  const [continueError, setContinueError] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Lock background scrolling while the Details popup is open.
  useEffect(() => {
    if (!selectedInvoiceJob) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedInvoiceJob]);

  // ---------------------------------------------------------------- loading
  useEffect(() => {
    let cancelled = false;

    const loadAll = async () => {
      try {
        const [
          jobsSnapshot,
          addItemSnapshot,
          totalsSnap,
          approvedSnap,
          depositSnap,
          invoicesSnap,
          propertiesSnap,
          usersSnap,
          contractorsSnap,
          monthlySnap,
        ] = await Promise.all([
          getDocs(collection(db, "jobs")),
          getDocs(query(collection(db, "addItem"), where("status", "==", "Completed"))),
          getDocs(collection(db, "job_totals")),
          getDocs(query(collection(db, "jobs"), where("status", "==", "Approved"))),
          getDocs(query(collection(db, "jobs"), where("serviceType", "==", "Additional Deposit"))),
          getDocs(collection(db, "approvedInvoices")),
          getDocs(collection(db, "properties")),
          getDocs(collection(db, "users")),
          getDocs(collection(db, "contractors")),
          getDocs(collection(db, "monthlyInvoice")),
        ]);

        if (cancelled) return;

        // __collection is written AFTER the spread so a stray stored field can
        // never overwrite the real source collection.
        setJobs([
          ...jobsSnapshot.docs.map((d) => ({ id: d.id, ...d.data(), __collection: "jobs" })),
          ...addItemSnapshot.docs.map((d) => ({ id: d.id, ...d.data(), __collection: "addItem" })),
        ]);

        const totalsMap = {};
        totalsSnap.forEach((d) => {
          totalsMap[d.id] = d.data().total;
        });
        setJobTotals(totalsMap);

        const depositMap = new Map();
        [...approvedSnap.docs, ...depositSnap.docs].forEach((d) => {
          depositMap.set(d.id, { id: d.id, ...d.data(), __collection: "jobs" });
        });
        setApprovedJobs(Array.from(depositMap.values()));

        setApprovedInvoices(invoicesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setPropertiesList(propertiesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

        const users = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsersList(users);
        const me = users.find((u) => u.id === auth.currentUser?.uid);
        if (me) setUserFullName(`${me.firstName || ""} ${me.lastName || ""}`.trim());

        setContractorsList(contractorsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setMonthlyJobs(monthlySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("Could not load Send Invoices data:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the shared materials list for the active job into the draft when
  // the Add/Edit Materials popup opens.
  useEffect(() => {
    if (!showMaterialsPopup || !selectedJob) return;
    const groupId = getMaterialsGroupId(selectedJob);
    (async () => {
      const currentMaterials = jobMaterials[groupId] || (await loadMaterialsForJob(selectedJob));
      const normalizedMaterials = currentMaterials.map((material) => ({
        ...material,
        name: material.name || material.product || "",
        qty: Number(material.qty ?? 1),
        unit: material.unit || "pcs",
        costPer: material.costPer ?? "",
        purchaseLocation: material.purchaseLocation || "",
        notes: material.notes || "",
        purchased: Boolean(material.purchased),
        files: Array.isArray(material.files) ? material.files : [],
      }));
      setJobMaterials((previous) => ({ ...previous, [groupId]: currentMaterials }));
      setMaterialsDraft(normalizedMaterials);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMaterialsPopup, selectedJob]);

  // ---------------------------------------------------------------- helpers
  const sortedPropertiesList = useMemo(
    () =>
      [...propertiesList].sort((a, b) =>
        (a.propertyName || a.siteName || "").localeCompare(b.propertyName || b.siteName || "")
      ),
    [propertiesList]
  );

  const siteOptions = sortedPropertiesList.map((property) => ({
    id: property.id,
    label: property.propertyName || property.siteName || "",
  }));

  const fillJobFromProperty = (selectedLabel, currentJob) => {
    const matchedProperty = propertiesList.find(
      (property) => property.propertyName === selectedLabel || property.siteName === selectedLabel
    );
    if (!matchedProperty) return { ...currentJob, siteName: selectedLabel };
    return {
      ...currentJob,
      siteName: matchedProperty.propertyName || matchedProperty.siteName || selectedLabel,
      propertyName: matchedProperty.propertyName || "",
      management: matchedProperty.management || currentJob.management || "",
      address: matchedProperty.address || "",
      city: matchedProperty.city || "",
      state: matchedProperty.state || "",
      zip: matchedProperty.zip || "",
    };
  };

  const getRowKey = (job) => job?.__rowKey || job?.id;

  const getTotalFor = (job) => jobTotals[String(job?.wo ?? job?.id)];

  /** Apply a patch to a job everywhere it is held in state. */
  const patchJobLocally = (jobId, patchOrFn) => {
    const apply = (job) =>
      typeof patchOrFn === "function" ? { ...job, ...patchOrFn(job) } : { ...job, ...patchOrFn };

    setJobs((prev) => prev.map((job) => (job.id === jobId ? apply(job) : job)));
    setApprovedJobs((prev) => prev.map((job) => (job.id === jobId ? apply(job) : job)));
    setSelectedInvoiceJob((prev) => (prev?.id === jobId ? apply(prev) : prev));
    setSelectedDepositJob((prev) => (prev?.id === jobId ? apply(prev) : prev));
    setSelectedJob((prev) => (prev?.id === jobId ? apply(prev) : prev));
  };

  const removeJobLocally = (jobId) => {
    setJobs((prev) => prev.filter((job) => job.id !== jobId));
    setApprovedJobs((prev) => prev.filter((job) => job.id !== jobId));
    setSelectedInvoiceJob((prev) => (prev?.id === jobId ? null : prev));
    setSelectedDepositJob((prev) => (prev?.id === jobId ? null : prev));
    setSelectedJob((prev) => (prev?.id === jobId ? null : prev));
  };

  const normalizeSearchText = (value) => (value || "").toString().toLowerCase().trim();

  const jobMatchesInvoiceSearch = (job, searchTerm) => {
    const term = normalizeSearchText(searchTerm);
    if (!term) return true;
    return [
      job.wo,
      job.clientWorkOrder,
      job.siteName,
      job.propertyName,
      job.scope,
      job.description,
      job.jobDescription,
      displayTechAssign(job.techAssign),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(term);
  };

  const filterJobsByInvoiceSearch = (list = [], searchTerm = "") =>
    list.filter((job) => jobMatchesInvoiceSearch(job, searchTerm));

  const sortData = (data, key, direction) => {
    if (!key) return data;
    return [...data].sort((a, b) => {
      const av = Array.isArray(a[key]) ? a[key].join(", ") : a[key] ?? "";
      const bv = Array.isArray(b[key]) ? b[key].join(", ") : b[key] ?? "";
      const result = String(av).localeCompare(String(bv), undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return direction === "ascending" ? result : -result;
    });
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") direction = "descending";
    setSortConfig({ key, direction });
  };

  // ------------------------------------------------------------ list data
  const completedRows = useMemo(() => jobs.flatMap(expandCompletedOccurrences), [jobs]);
  const sentRows = useMemo(() => jobs.flatMap(expandInvoicedOccurrences), [jobs]);

  const visibleCompletedRows = sortData(
    filterJobsByInvoiceSearch(completedRows, sendInvoicesSearch),
    sortConfig.key,
    sortConfig.direction
  );

  const visibleSentRows = sortData(
    filterJobsByInvoiceSearch(sentRows, sendInvoicesSearch),
    sortConfig.key,
    sortConfig.direction
  );

  const invoiceIndex = useMemo(() => indexInvoices(approvedInvoices), [approvedInvoices]);

  const rowInvoiceInfo = (row) => {
    const invoices = getRowInvoices(row, invoiceIndex);
    const latest = invoices[0] || null;
    const legacyFlag = !isRecurring(row) && row?.approvedInvoice === true;
    return {
      invoices,
      latest,
      hasInvoice: Boolean(latest) || legacyFlag,
      status: latest ? invoiceStatusLabel(latest) : legacyFlag ? "Approved" : "",
    };
  };

  const awaitingCount = completedRows.filter((row) => !rowInvoiceInfo(row).hasInvoice).length;
  const draftedCount = completedRows.length - awaitingCount;

  // ------------------------------------------------------------ totals
  const openTotalPopup = (job) => {
    setSelectedJob(job);
    setTotalInput(getTotalFor(job) ?? "");
    setShowTotalPopup(true);
  };

  const saveTotal = async () => {
    if (!selectedJob || totalInput === "") return;
    const jobKey = String(selectedJob.wo ?? selectedJob.id);

    try {
      await addJobTotal({
        jobId: jobKey,
        wo: selectedJob.wo ? String(selectedJob.wo) : null,
        proposalId: selectedJob.proposalId || null,
        total: Number(totalInput),
        source: "job",
      });

      await updateDoc(doc(db, getJobCollectionName(selectedJob), selectedJob.id), {
        estimatedTotal: Number(totalInput),
      });

      setJobTotals((prev) => ({ ...prev, [jobKey]: Number(totalInput) }));
      patchJobLocally(selectedJob.id, { estimatedTotal: Number(totalInput) });
      setShowTotalPopup(false);
    } catch (error) {
      console.error("Error saving total:", error);
      alert("Could not save the total.");
    }
  };

  // ------------------------------------------------------------ continue
  const openContinuePopup = async (job) => {
    if (!job?.id) return;
    setContinueSourceJob(job);
    setContinueNextWO("");
    setContinueError("");
    setContinueLoading(true);
    try {
      setContinueNextWO(await getNextContinuedWO(job.wo));
    } catch (err) {
      console.error("Could not work out the next work order:", err);
      setContinueError("Could not work out the next work order number.");
    } finally {
      setContinueLoading(false);
    }
  };

  const closeContinuePopup = () => {
    setContinueSourceJob(null);
    setContinueNextWO("");
    setContinueError("");
  };

  const confirmContinue = async (newDate) => {
    if (!continueSourceJob?.id) return;
    setContinueSaving(true);
    setContinueError("");
    try {
      const created = await createContinuationJob({
        sourceJob: continueSourceJob,
        newDate,
        newWO: continueNextWO,
      });
      setJobs((prev) => [...prev, { ...created.job, __collection: "jobs" }]);
      const fromWO = continueSourceJob.wo || "The original job";
      closeContinuePopup();
      alert(`New active job ${created.wo} created for ${newDate}.\n\n${fromWO} was not changed.`);
    } catch (err) {
      console.error("Could not continue this job:", err);
      setContinueError(err?.message || "Could not create the new job.");
    } finally {
      setContinueSaving(false);
    }
  };

  // ------------------------------------------------------------ job actions
  const markSourceProposalCompleted = async (job) => {
    if (!job?.proposalId) return;
    try {
      await updateDoc(doc(db, "proposals", job.proposalId), {
        status: "Completed",
        completedFromJobId: job.id || "",
        completedFromWO: job.wo || "",
        proposalCompletedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Could not mark source proposal completed:", err);
    }
  };

  const markProjectFullyCompleted = async (targetJob) => {
    if (!targetJob?.id) return;
    const completionDate = window.prompt("Enter the final project completion date:", todayKey());
    if (!completionDate) return;
    const ok = window.confirm(
      `Mark this entire project completed as of ${completionDate}? This will not create another project day.`
    );
    if (!ok) return;

    try {
      await updateDoc(doc(db, getJobCollectionName(targetJob), targetJob.id), {
        status: "Completed",
        isProject: true,
        projectStatus: "Project Completed",
        projectCompletedAt: completionDate,
        projectCompletedBy: userFullName || "User",
        completedDates: arrayUnion(completionDate),
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(targetJob);

      patchJobLocally(targetJob.id, (job) => ({
        status: "Completed",
        isProject: true,
        projectStatus: "Project Completed",
        projectCompletedAt: completionDate,
        projectCompletedBy: userFullName || "User",
        completedDates: Array.from(new Set([...(job.completedDates || []), completionDate])),
      }));
      alert("Project marked completed.");
    } catch (err) {
      console.error("Error completing project:", err);
      alert("Could not mark project completed.");
    }
  };

  const addSupervisorNote = async (targetJob) => {
    if (!targetJob?.id) return;
    const text = window.prompt("Add supervisor note:");
    if (!text || !text.trim()) return;

    const newSupervisorNote = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      text: text.trim(),
      author: userFullName || "User",
      createdAt: new Date().toISOString(),
      checked: false,
      checkedBy: "",
      checkedAt: "",
      occurrenceDate: isRecurring(targetJob) ? getCompletionOccurrenceKey(targetJob) : null,
    };

    try {
      await updateDoc(doc(db, getJobCollectionName(targetJob), targetJob.id), {
        supervisorNotes: arrayUnion(newSupervisorNote),
      });
      patchJobLocally(targetJob.id, (job) => ({
        supervisorNotes: [...(Array.isArray(job.supervisorNotes) ? job.supervisorNotes : []), newSupervisorNote],
      }));
    } catch (err) {
      console.error("Error saving supervisor note:", err);
      alert("Could not save the supervisor note.");
    }
  };

  // Materials
  const addMaterial = () => {
    setMaterialsDraft((prev) => [
      ...prev,
      { name: "", qty: 1, unit: "pcs", costPer: "", purchaseLocation: "", notes: "", purchased: false, files: [] },
    ]);
  };

  const updateMaterialDraft = (index, patch) => {
    setMaterialsDraft((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const removeMaterialDraft = (index) => {
    setMaterialsDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadMaterialFiles = async (index, fileList) => {
    if (!selectedJob) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const groupId = getMaterialsGroupId(selectedJob);
    const uploaded = [];
    try {
      for (const file of files) {
        // jobs/{id}/{file} is an allowed path in Priority's storage.rules.
        const fileRef = ref(getStorage(), `jobs/${groupId}/material_${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        uploaded.push({ url: await getDownloadURL(fileRef), name: file.name });
      }
    } catch (err) {
      console.error("Material upload failed:", err);
      alert("Could not upload one of the files.");
    }
    setMaterialsDraft((previous) =>
      previous.map((m, i) => (i === index ? { ...m, files: [...(m.files || []), ...uploaded] } : m))
    );
  };

  const removeMaterialFile = (materialIndex, fileIndex) => {
    setMaterialsDraft((previous) =>
      previous.map((m, i) =>
        i === materialIndex ? { ...m, files: (m.files || []).filter((_, fi) => fi !== fileIndex) } : m
      )
    );
  };

  const getEstimatedMaterialCost = (material) =>
    Number(material?.qty || 0) * Number(material?.costPer || 0);

  const saveMaterials = async () => {
    if (!selectedJob?.id) return;
    try {
      const groupId = getMaterialsGroupId(selectedJob);
      await saveMaterialsForJob(selectedJob, materialsDraft);
      setJobMaterials((prev) => ({ ...prev, [groupId]: materialsDraft }));
      setShowMaterialsPopup(false);
    } catch (err) {
      console.error("Error saving materials:", err);
      alert("Could not save materials. Please try again.");
    }
  };

  // Receipts
  const saveReceipt = async () => {
    if (!selectedJob?.id) return;
    if (!receiptAmount && !receiptFile) {
      alert("Please add a receipt amount or upload a receipt image.");
      return;
    }
    setSavingReceipt(true);
    try {
      let fileURL = "";
      let fileName = "";
      if (receiptFile) {
        const fileRef = ref(
          getStorage(),
          `jobs/${selectedJob.id}/receipt_${Date.now()}_${receiptFile.name}`
        );
        await uploadBytes(fileRef, receiptFile);
        fileURL = await getDownloadURL(fileRef);
        fileName = receiptFile.name;
      }

      const newReceipt = {
        amount: Number(receiptAmount || 0),
        note: receiptNote || "",
        fileURL,
        fileName,
        uploadedBy: userFullName || "User",
        uploadedAt: new Date().toISOString(),
      };

      const updatedReceipts = Array.isArray(selectedJob.receipts)
        ? [...selectedJob.receipts, newReceipt]
        : [newReceipt];
      const receiptsTotal = updatedReceipts.reduce((sum, r) => sum + Number(r.amount || 0), 0);

      await updateDoc(doc(db, getJobCollectionName(selectedJob), selectedJob.id), {
        receipts: updatedReceipts,
        receiptsTotal,
        updatedAt: serverTimestamp(),
      });

      patchJobLocally(selectedJob.id, { receipts: updatedReceipts, receiptsTotal });
      setReceiptAmount("");
      setReceiptFile(null);
      setReceiptNote("");
      setShowReceiptPopup(false);
    } catch (err) {
      console.error("Error saving receipt:", err);
      alert("Could not save receipt.");
    } finally {
      setSavingReceipt(false);
    }
  };

  // ------------------------------------------------------------ invoicing
  const openCreateInvoice = (row) => {
    if (!row?.id) return;
    if (getJobCollectionName(row) === "addItem") {
      alert(
        "This item was added from Add Item, not scheduled as a job, so the completion invoice form cannot update it. Use Invoice Sent once it has been billed."
      );
      return;
    }
    const info = rowInvoiceInfo(row);
    if (info.latest) {
      const ok = window.confirm(
        `An invoice for this ${isRecurring(row) ? "visit" : "job"} is already in Approved Invoices (status: ${info.status}).\n\nCreate another one anyway?`
      );
      if (!ok) return;
    }
    setInvoiceModalJob(row);
  };

  const handleInvoiceCreated = async (row, invoice) => {
    if (!invoice?.id) return;
    const localInvoice = { ...invoice, createdAt: new Date().toISOString() };
    setApprovedInvoices((prev) => [localInvoice, ...prev.filter((inv) => inv.id !== invoice.id)]);

    // Mirror what the form wrote on the job, so the row turns green without a reload.
    patchJobLocally(row.id, (job) => {
      const patch = {
        approvedInvoiceId: invoice.id,
        invoiceStatus: invoice.status,
        estimatedTotal: Number(invoice.amount || 0),
      };
      if (isRecurring(job)) {
        // Canonical occurrence key, the same one the form wrote.
        const key = getCompletionOccurrenceKey(row);
        if (key) patch.completedDates = Array.from(new Set([...(job.completedDates || []), key]));
      } else {
        patch.status = "Completed";
      }
      return patch;
    });
  };

  const goToApprovedInvoices = () => navigate("/ApprovedInvoices");

  /**
   * Manual "Invoice Sent" for an invoice that went out some other way.
   * One-time job: status -> "Invoice Sent".
   * Recurring:    ONLY this occurrence's canonical key is added to invoicedDates.
   */
  const handleInvoiceSent = async (row) => {
    try {
      if (!row?.id) throw new Error("Job ID is undefined");
      const collectionName = getJobCollectionName(row);
      const jobRef = doc(db, collectionName, row.id);
      const snap = await getDoc(jobRef);
      if (!snap.exists()) throw new Error(`This record no longer exists in "${collectionName}".`);

      if (isRecurring(row)) {
        const key = getCompletionOccurrenceKey(row);
        if (!key) throw new Error("Could not tell which visit this row is for.");
        await updateDoc(jobRef, {
          invoicedDates: arrayUnion(key),
          lastInvoiceSentAt: serverTimestamp(),
        });
        patchJobLocally(row.id, (job) => ({
          invoicedDates: Array.from(new Set([...(job.invoicedDates || []), key])),
        }));
      } else {
        await updateDoc(jobRef, { status: "Invoice Sent", lastInvoiceSentAt: serverTimestamp() });
        patchJobLocally(row.id, { status: "Invoice Sent" });
      }

      // Optionally close out the matching approved invoice too, so it does
      // not sit in Approved Invoices waiting to be emailed again.
      const info = rowInvoiceInfo(row);
      if (alsoMarkInvoiceSent && info.latest && invoiceStatusLabel(info.latest) !== "Sent" && invoiceStatusLabel(info.latest) !== "Paid") {
        const stamp = new Date().toISOString();
        const patch = {
          status: "Sent",
          sent: true,
          sentAt: stamp,
          sentMethod: "Marked from Send Invoices",
          updatedAt: stamp,
        };
        await updateDoc(doc(db, "approvedInvoices", info.latest.id), patch);
        setApprovedInvoices((prev) =>
          prev.map((inv) => (inv.id === info.latest.id ? { ...inv, ...patch } : inv))
        );
      }

      setShowVerificationPopup(false);
      setSelectedJob(null);
      setSelectedInvoiceJob(null);
      setIsEditing(false);
      setEditedJob(null);
      setEditingMode(null);
    } catch (error) {
      console.error("Error updating job status:", error);
      alert(`Could not mark this job as Invoice Sent.\n\n${error?.message || error}`);
    }
  };

  const moveInvoiceSentBackToCompleted = async () => {
    const row = jobToUnsend;
    if (!row?.id) return;
    try {
      const jobRef = doc(db, getJobCollectionName(row), row.id);
      if (isRecurring(row)) {
        const keys = rowOccurrenceKeys(row);
        await updateDoc(jobRef, {
          invoicedDates: arrayRemove(...keys),
          invoiceSentReversedAt: serverTimestamp(),
        });
        patchJobLocally(row.id, (job) => ({
          invoicedDates: (job.invoicedDates || []).filter((d) => !keys.includes(toDateKey(d))),
        }));
      } else {
        await updateDoc(jobRef, { status: "Completed", invoiceSentReversedAt: serverTimestamp() });
        patchJobLocally(row.id, { status: "Completed" });
      }
      setJobToUnsend(null);
      alert("Moved back to Send Invoices.");
    } catch (error) {
      console.error("Error moving invoice back to Completed:", error);
      alert("Failed to move invoice back to Completed.");
    }
  };

  /** Remove the approved invoice(s) for this row so it can be redone. */
  const unapproveInvoice = async () => {
    const row = jobToUnapprove;
    if (!row?.id) return;
    try {
      const { invoices } = rowInvoiceInfo(row);
      const removable = invoices.filter((inv) => !["Sent", "Paid"].includes(invoiceStatusLabel(inv)));
      await Promise.all(removable.map((inv) => deleteDoc(doc(db, "approvedInvoices", inv.id))));
      const removedIds = removable.map((inv) => inv.id);

      const jobPatch = { invoiceUnapprovedAt: serverTimestamp() };
      const localPatch = {};
      if (!isRecurring(row) || removedIds.includes(row.approvedInvoiceId)) {
        jobPatch.approvedInvoiceId = "";
        jobPatch.invoiceStatus = "";
        jobPatch.approvedInvoice = false;
        Object.assign(localPatch, { approvedInvoiceId: "", invoiceStatus: "", approvedInvoice: false });
      }
      await updateDoc(doc(db, getJobCollectionName(row), row.id), jobPatch);

      setApprovedInvoices((prev) => prev.filter((inv) => !removedIds.includes(inv.id)));
      patchJobLocally(row.id, localPatch);
      setJobToUnapprove(null);
      alert(
        removable.length < invoices.length
          ? "Draft/approved invoices removed. Invoices that were already sent or paid were left in place."
          : "Invoice removed from Approved Invoices."
      );
    } catch (error) {
      console.error("Error un-approving invoice:", error);
      alert("Failed to un-approve invoice.");
    }
  };

  const handleDepositSent = async (id) => {
    try {
      await updateDoc(doc(db, "jobs", id), {
        status: "Active",
        serviceType: "Approved Proposals",
        startDate: null,
        endDate: null,
      });
      setApprovedJobs((prev) => prev.filter((job) => job.id !== id));
      setSelectedDepositJob(null);
      setSelectedJob(null);
      setShowVerificationPopup(false);
    } catch (error) {
      console.error("Error updating job status:", error);
      alert("Could not mark the deposit as sent.");
    }
  };

  const handleShowVerificationPopup = (job, mode = "invoice") => {
    if (!job?.id) return;
    setSelectedJob(job);
    setAlsoMarkInvoiceSent(true);
    setShowVerificationPopup(mode === "deposit" ? "deposit" : "invoice");
  };

  const handleCloseVerificationPopup = () => {
    setShowVerificationPopup(false);
    setSelectedJob(null);
  };

  // ------------------------------------------------------------ editing
  const handleEdit = (job, mode = "job") => {
    setIsEditing(true);
    setEditingMode(mode);
    setEditedJob({ ...job, techAssign: normalizeTechAssign(job.techAssign) });
    if (mode === "monthly") setSelectedMonthlyJob(job);
    else setSelectedJob(job);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedJob(null);
    setEditingMode(null);
  };

  const handleSave = async () => {
    if (!editedJob) return;
    setSavingEdit(true);
    try {
      if (editingMode === "monthly") {
        const payload = { ...editedJob };
        delete payload.id;
        await updateDoc(doc(db, "monthlyInvoice", editedJob.id), payload);
        setMonthlyJobs((prev) => prev.map((job) => (job.id === editedJob.id ? editedJob : job)));
        setSelectedMonthlyJob(editedJob);
        handleCancel();
        return;
      }

      const originalJob = selectedInvoiceJob || selectedDepositJob || selectedJob || editedJob;
      const edited = { ...editedJob, techAssign: normalizeTechAssign(editedJob.techAssign) };
      delete edited.__collection;

      if (getJobCollectionName(originalJob) === "addItem") {
        const payload = { ...edited };
        ["id", "__rowKey", "__occurrenceRow", "occurrenceDate", "originalOccurrenceDate"].forEach(
          (key) => delete payload[key]
        );
        await updateDoc(doc(db, "addItem", originalJob.id), payload);
        patchJobLocally(originalJob.id, payload);
        handleCancel();
        return;
      }

      // A recurring occurrence row's status/completedAt are synthesized by the
      // list, not real fields - never echo them back onto the series.
      if (isRecurring(originalJob) && originalJob.__occurrenceRow) {
        delete edited.status;
        delete edited.completedAt;
      }

      const result = await saveRecurringJobEdit({ originalJob, editedJob: edited });
      if (result.mode === "cancel") return;

      if (result.mode === "future") {
        setJobs((prev) => [
          ...prev.map((job) =>
            job.id === result.oldJob.id ? { ...result.oldJob, __collection: "jobs" } : job
          ),
          { ...result.newJob, __collection: "jobs" },
        ]);
        setSelectedInvoiceJob(null);
      } else if (result.mode === "occurrence") {
        const master = { ...result.masterJob, __collection: "jobs" };
        setJobs((prev) => prev.map((job) => (job.id === master.id ? master : job)));
        setSelectedInvoiceJob((prev) =>
          prev?.id === master.id ? { ...prev, ...result.updatedJob } : prev
        );
      } else {
        const updated = { ...result.updatedJob, __collection: "jobs" };
        setJobs((prev) => prev.map((job) => (job.id === updated.id ? { ...job, ...updated } : job)));
        setApprovedJobs((prev) =>
          prev.map((job) => (job.id === updated.id ? { ...job, ...updated } : job))
        );
        setSelectedInvoiceJob((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
        setSelectedDepositJob((prev) => (prev?.id === updated.id ? { ...prev, ...updated } : prev));
      }

      handleCancel();
    } catch (error) {
      console.error("Error saving job:", error);
      alert("Could not save job changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ------------------------------------------------------------ monthly
  const handleContractClosed = async (job) => {
    try {
      await updateDoc(doc(db, "monthlyInvoice", job.id), { status: "Closed" });
      setMonthlyJobs((prev) => prev.map((item) => (item.id === job.id ? { ...item, status: "Closed" } : item)));
      setSelectedMonthlyJob((prev) => (prev?.id === job.id ? { ...prev, status: "Closed" } : prev));
    } catch (error) {
      console.error("Error closing contract:", error);
    }
  };

  const handleAddMonthlyJob = async () => {
    try {
      const docRef = await addDoc(collection(db, "monthlyInvoice"), newMonthlyJob);
      setMonthlyJobs((prev) => [...prev, { ...newMonthlyJob, id: docRef.id }]);
      setShowAddMonthlyJobPopup(false);
      setNewMonthlyJob(EMPTY_MONTHLY_JOB);
      alert("New Monthly Job added");
    } catch (error) {
      console.error("Error adding monthly job:", error);
      alert("Could not add the monthly job.");
    }
  };

  const handleMonthlyJobInputChange = (e) => {
    const { name, value } = e.target;
    setNewMonthlyJob((prev) => ({ ...prev, [name]: value }));
  };

  const handlePropertyChange = (e) => {
    const selectedProperty = propertiesList.find(
      (property) => property.propertyName === e.target.value
    );
    if (selectedProperty) {
      setNewMonthlyJob((prev) => ({
        ...prev,
        siteName: selectedProperty.propertyName,
        management: selectedProperty.management || "",
        address: selectedProperty.address || "",
        city: selectedProperty.city || "",
        state: selectedProperty.state || "",
        zip: selectedProperty.zip || "",
      }));
    }
  };

  const handleMonthlyInvoiceInputChange = (e) => {
    const { name, value } = e.target;
    setInvoiceDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleMonthlyInvoiceSubmit = async () => {
    if (!selectedMonthlyJob?.id) return;
    try {
      const newNote = {
        text: `Invoice Month: ${invoiceDetails.invoiceMonth}, Date Invoice Sent: ${invoiceDetails.dateInvoiceSent}, Payment Number: ${invoiceDetails.paymentNumber}`,
        timestamp: new Date().toLocaleString(),
        author: "System",
      };
      const updatedNotes = Array.isArray(selectedMonthlyJob.notes)
        ? [...selectedMonthlyJob.notes, newNote]
        : [newNote];
      await updateDoc(doc(db, "monthlyInvoice", selectedMonthlyJob.id), { notes: updatedNotes });
      setSelectedMonthlyJob((prev) => ({ ...prev, notes: updatedNotes }));
      setMonthlyJobs((prev) =>
        prev.map((job) => (job.id === selectedMonthlyJob.id ? { ...job, notes: updatedNotes } : job))
      );
      setShowMonthlyInvoicePopup(false);
      alert("Monthly Invoice added");
    } catch (error) {
      console.error("Error adding monthly invoice:", error);
      alert("Could not add the monthly invoice.");
    }
  };

  // ------------------------------------------------------------ delete
  const handleDeleteRequest = async (row) => {
    if (!row?.id) return;
    if (isRecurring(row)) {
      // Recurring jobs get the shared Close Job scope dialog (this visit /
      // this and future / whole series).
      try {
        const result = await closeOrDeleteJob({ job: row, collectionName: getJobCollectionName(row) });
        if (result.mode === "cancel") return;
        if (result.mode === "deleted") {
          removeJobLocally(row.id);
        } else if (result.mode === "occurrence") {
          // A cancelled visit is not a completed one - take it off this list.
          const keys = rowOccurrenceKeys(row);
          await updateDoc(doc(db, getJobCollectionName(row), row.id), {
            completedDates: arrayRemove(...keys),
          });
          patchJobLocally(row.id, (job) => ({
            excludedDates: result.updatedJob?.excludedDates || job.excludedDates,
            completedDates: (job.completedDates || []).filter((d) => !keys.includes(toDateKey(d))),
          }));
          setSelectedInvoiceJob(null);
        } else if (result.updatedJob) {
          patchJobLocally(row.id, { recurrence: result.updatedJob.recurrence });
        }
      } catch (error) {
        console.error("Error closing job:", error);
        alert("Could not close this job.");
      }
      return;
    }
    setSelectedJob(row);
    setShowDeleteVerificationPopup(true);
  };

  const handleDeleteJob = async (row) => {
    if (!row?.id) return;
    try {
      await deleteDoc(doc(db, getJobCollectionName(row), row.id));
      removeJobLocally(row.id);
      setShowDeleteVerificationPopup(false);
    } catch (error) {
      console.error("Error deleting job:", error);
      alert("Could not delete the job.");
    }
  };

  // ------------------------------------------------------------ notes
  const handleFileChange = (e) => setSelectedFiles(Array.from(e.target.files || []));

  const uploadNoteFiles = async (files, ownerId) => {
    const storage = getStorage();
    return Promise.all(
      files.map(async (file) => {
        const storageRef = ref(storage, `notes/${ownerId}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        return { fileURL: await getDownloadURL(storageRef), fileName: file.name };
      })
    );
  };

  const handleShowNotePopup = (job, target = "job") => {
    if (!job?.id) return;
    if (target === "job") setSelectedJob(job);
    setNoteTarget(target);
    setShowNotePopup(true);
  };

  const closeNotePopup = () => {
    setShowNotePopup(false);
    setNoteText("");
    setSelectedFiles([]);
    setNoteTarget(null);
  };

  const saveNote = async () => {
    const isMonthly = noteTarget === "monthly";
    const target = isMonthly ? selectedMonthlyJob : selectedJob;
    if (!target?.id || (noteText.trim() === "" && selectedFiles.length === 0)) return;

    setUploading(true);
    try {
      const uploadedFiles = selectedFiles.length ? await uploadNoteFiles(selectedFiles, target.id) : [];
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: userFullName || auth.currentUser?.email || "Unknown User",
        files: uploadedFiles,
      };
      const updatedNotes = Array.isArray(target.notes) ? [...target.notes, newNote] : [newNote];

      if (isMonthly) {
        await updateDoc(doc(db, "monthlyInvoice", target.id), { notes: updatedNotes });
        setSelectedMonthlyJob((prev) => ({ ...prev, notes: updatedNotes }));
        setMonthlyJobs((prev) => prev.map((job) => (job.id === target.id ? { ...job, notes: updatedNotes } : job)));
      } else {
        const collectionName = getJobCollectionName(target);
        await updateDoc(doc(db, collectionName, target.id), { notes: updatedNotes });
        patchJobLocally(target.id, { notes: updatedNotes });

        if (collectionName === "jobs") {
          try {
            await sendJobNoteEmailCallable({
              jobId: target.id,
              noteIndex: updatedNotes.length - 1,
              collectionName: "jobs",
            });
          } catch (emailErr) {
            console.error("Note saved, but note email failed:", emailErr);
          }
        }
      }
      closeNotePopup();
    } catch (err) {
      console.error("Error saving note:", err);
      alert("There was an error saving your note. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveDepositPrice = async () => {
    const target = selectedDepositJob;
    if (!target?.id || !depositPrice) return;
    try {
      await updateDoc(doc(db, "jobs", target.id), { depositPrice: parseFloat(depositPrice) });
      patchJobLocally(target.id, { depositPrice: parseFloat(depositPrice) });
      setShowDepositPricePopup(false);
      setDepositPrice("");
    } catch (error) {
      console.error("Error saving deposit price:", error);
      alert("Could not save the deposit price.");
    }
  };

  const calculateRemainingBalance = (job) => {
    let remainingBalance = Number(job.proposalPrice || 0);
    if (job.depositPrice) remainingBalance -= Number(job.depositPrice);
    if (job.depositPrice2) remainingBalance -= Number(job.depositPrice2);
    if (job.changePrice) remainingBalance += Number(job.changePrice);
    if (job.changePrice2) remainingBalance += Number(job.changePrice2);
    return remainingBalance;
  };

  // ------------------------------------------------------------ bulk
  const toggleJobSelection = (rowKey) => {
    setSelectedJobIds((prev) =>
      prev.includes(rowKey) ? prev.filter((id) => id !== rowKey) : [...prev, rowKey]
    );
  };

  const visibleRowKeys = visibleCompletedRows.map(getRowKey);
  const allVisibleSelected =
    visibleRowKeys.length > 0 && visibleRowKeys.every((key) => selectedJobIds.includes(key));

  const toggleSelectAllJobs = () => {
    setSelectedJobIds(allVisibleSelected ? [] : visibleRowKeys);
  };

  const selectedRows = () => completedRows.filter((row) => selectedJobIds.includes(getRowKey(row)));

  const bulkUpdateStatus = async (newStatus) => {
    const rows = selectedRows();
    if (!rows.length) return;

    const failures = [];
    for (const row of rows) {
      try {
        const jobRef = doc(db, getJobCollectionName(row), row.id);
        if (isRecurring(row)) {
          const keys = rowOccurrenceKeys(row);
          if (newStatus === "Invoice Sent") {
            const key = getCompletionOccurrenceKey(row);
            await updateDoc(jobRef, { invoicedDates: arrayUnion(key), lastInvoiceSentAt: serverTimestamp() });
            patchJobLocally(row.id, (job) => ({
              invoicedDates: Array.from(new Set([...(job.invoicedDates || []), key])),
            }));
          } else {
            // "Active" = reopen just this visit.
            await updateDoc(jobRef, {
              completedDates: arrayRemove(...keys),
              invoicedDates: arrayRemove(...keys),
            });
            patchJobLocally(row.id, (job) => ({
              completedDates: (job.completedDates || []).filter((d) => !keys.includes(toDateKey(d))),
              invoicedDates: (job.invoicedDates || []).filter((d) => !keys.includes(toDateKey(d))),
            }));
          }
        } else {
          await updateDoc(jobRef, { status: newStatus, updatedAt: serverTimestamp() });
          patchJobLocally(row.id, { status: newStatus });
        }
      } catch (error) {
        console.error("Bulk update failed for", row.id, error);
        failures.push(row.wo || row.siteName || row.id);
      }
    }

    setSelectedJobIds([]);
    if (failures.length) alert(`Could not update: ${failures.join(", ")}`);
  };

  const bulkDeleteJobs = async () => {
    const rows = selectedRows();
    const oneTime = rows.filter((row) => !isRecurring(row));
    const skipped = rows.length - oneTime.length;

    try {
      await Promise.all(
        oneTime.map((row) => deleteDoc(doc(db, getJobCollectionName(row), row.id)))
      );
      oneTime.forEach((row) => removeJobLocally(row.id));
    } catch (error) {
      console.error("Error bulk-deleting jobs:", error);
      alert("Some jobs could not be deleted.");
    }

    setSelectedJobIds([]);
    if (skipped) {
      alert(
        `${skipped} recurring visit${skipped === 1 ? " was" : "s were"} skipped. Open the job's Details and use Delete to choose whether to close one visit or the whole series.`
      );
    }
  };

  const handleBulkStatusClick = (status) => {
    if (!selectedJobIds.length) return;
    setBulkActionType("status");
    setBulkStatusTarget(status);
    setShowBulkConfirm(true);
  };

  const handleBulkDeleteClick = () => {
    if (!selectedJobIds.length) return;
    setBulkActionType("delete");
    setBulkStatusTarget(null);
    setShowBulkConfirm(true);
  };

  const handleConfirmBulkAction = async () => {
    setBulkWorking(true);
    try {
      if (bulkActionType === "status" && bulkStatusTarget) await bulkUpdateStatus(bulkStatusTarget);
      else if (bulkActionType === "delete") await bulkDeleteJobs();
    } finally {
      setBulkWorking(false);
      setShowBulkConfirm(false);
      setBulkActionType(null);
      setBulkStatusTarget(null);
    }
  };

  const handleCancelBulkAction = () => {
    setSelectedJobIds([]);
    setShowBulkConfirm(false);
    setBulkActionType(null);
    setBulkStatusTarget(null);
  };

  const openFinancials = (job) => {
    if (!job?.id) return;
    navigate(`/JobFinancials/${job.id}`, {
      state: { jobId: job.id, wo: job.wo || "", siteName: job.siteName || "" },
    });
  };

  // ------------------------------------------------------------ rendering bits
  const renderNotes = (notes) =>
    Array.isArray(notes) && notes.length > 0 ? (
      <ul className="si-notes">
        {notes.map((note, index) => (
          <li key={index}>
            <strong>
              {note.author} ({note.timestamp}):
            </strong>{" "}
            {note.text}
            {Array.isArray(note.files) &&
              note.files.map((file, fileIndex) => {
                const url = typeof file === "string" ? file : file?.fileURL || file?.url;
                if (!url) return null;
                return (
                  <p key={fileIndex} style={{ margin: "6px 0 0" }}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {isImageUrl(url) ? (
                        <img src={url} alt={`Attachment ${fileIndex + 1}`} className="si-thumb" />
                      ) : (
                        `View Attachment ${fileIndex + 1}`
                      )}
                    </a>
                  </p>
                );
              })}
          </li>
        ))}
      </ul>
    ) : (
      <p className="si-muted">No notes yet.</p>
    );

  const renderUploadedFiles = (job) =>
    Array.isArray(job.fileURLs) && job.fileURLs.length > 0 ? (
      <div className="si-section">
        <h4 className="si-section-title">Uploaded Files</h4>
        <div className="si-files">
          {job.fileURLs.map((fileURL, index) => (
            <div key={index}>
              <a href={fileURL} target="_blank" rel="noopener noreferrer">
                {isImageUrl(fileURL) ? (
                  <img src={fileURL} alt={`File ${index + 1}`} className="si-thumb" />
                ) : (
                  `View File ${index + 1}`
                )}
              </a>
              <p className="si-muted" style={{ margin: "4px 0 0" }}>
                {job.fileNames && job.fileNames[index]}
              </p>
            </div>
          ))}
        </div>
      </div>
    ) : null;

  const renderPriceLines = (job) => (
    <>
      {job.proposalPrice ? <DetailRow label="Proposal Price" value={formatMoney(job.proposalPrice)} /> : null}
      {job.depositPrice ? <DetailRow label="Deposit Price" value={formatMoney(job.depositPrice)} /> : null}
      {job.depositPrice2 ? <DetailRow label="Deposit Price 2" value={formatMoney(job.depositPrice2)} /> : null}
      {job.changePrice ? <DetailRow label="Change Price" value={formatMoney(job.changePrice)} /> : null}
      {job.changePrice2 ? <DetailRow label="Change Price 2" value={formatMoney(job.changePrice2)} /> : null}
      {job.proposalPrice ? (
        <DetailRow label="Remaining Balance" value={formatMoney(calculateRemainingBalance(job))} />
      ) : null}
    </>
  );

  const detailsJob = selectedInvoiceJob;
  const detailsInvoiceInfo = detailsJob ? rowInvoiceInfo(detailsJob) : null;
  const detailsIsSent =
    detailsJob &&
    (isRecurring(detailsJob)
      ? rowOccurrenceKeys(detailsJob).some((key) => getInvoicedDateSet(detailsJob).has(key))
      : detailsJob.status === "Invoice Sent");

  const verificationRow = selectedJob;
  const verificationInvoice =
    showVerificationPopup === "invoice" && verificationRow ? rowInvoiceInfo(verificationRow).latest : null;

  return (
    <div className="si-page">
      {/* ================= Page header ================= */}
      <header className="si-header">
        <div>
          <h2 className="si-title">Send Invoices</h2>
          <p className="si-subtitle">
            Completed jobs waiting to be invoiced. Create the invoice here, then review and email it
            from Approved Invoices.
          </p>
        </div>
        <div className="si-header-actions">
          <button type="button" className="si-btn si-btn--blue" onClick={goToApprovedInvoices}>
            Approved Invoices →
          </button>
          <button type="button" className="si-btn si-btn--outline" onClick={() => setShowAddMonthlyJobPopup(true)}>
            + Monthly Job
          </button>
        </div>
      </header>

      <div className="si-stats">
        <div className="si-stat">
          <span className="si-stat-label">Needs an invoice</span>
          <span className="si-stat-value">{awaitingCount}</span>
        </div>
        <div className="si-stat">
          <span className="si-stat-label">Invoice in review</span>
          <span className="si-stat-value si-stat-value--green">{draftedCount}</span>
        </div>
        <div className="si-stat">
          <span className="si-stat-label">Sent</span>
          <span className="si-stat-value si-stat-value--blue">{sentRows.length}</span>
        </div>
      </div>

      {/* ================= Send Invoices ================= */}
      <section className="si-card">
        <div className="si-card-head">
          <h3 className="si-card-title">Send Invoices</h3>
          <div className="si-search">
            <input
              type="text"
              value={sendInvoicesSearch}
              onChange={(e) => setSendInvoicesSearch(e.target.value)}
              placeholder="Search by WO, site name, scope, technician, or description..."
            />
          </div>
        </div>

        <div className="si-bulk">
          <div className="si-bulk-left">
            <label className="si-check">
              <input type="checkbox" onChange={toggleSelectAllJobs} checked={allVisibleSelected} />
              Select All
            </label>
            {selectedJobIds.length > 0 && (
              <span className="si-muted">
                {selectedJobIds.length} job{selectedJobIds.length > 1 ? "s" : ""} selected
              </span>
            )}
          </div>
          <div className="si-bulk-right">
            <button
              type="button"
              className="si-btn si-btn--blue si-btn--sm"
              disabled={!selectedJobIds.length}
              onClick={() => handleBulkStatusClick("Invoice Sent")}
            >
              Mark Selected as Invoice Sent
            </button>
            <button
              type="button"
              className="si-btn si-btn--grey si-btn--sm"
              disabled={!selectedJobIds.length}
              onClick={() => handleBulkStatusClick("Active")}
            >
              Mark Selected as Active
            </button>
            <button
              type="button"
              className="si-btn si-btn--red si-btn--sm"
              disabled={!selectedJobIds.length}
              onClick={handleBulkDeleteClick}
            >
              Delete Selected
            </button>
          </div>
        </div>

        <div className="si-table-wrap" style={{ maxHeight: "70vh" }}>
          <table className="si-table si-table--main">
            <thead>
              <tr>
                <th className="si-col-select">Select</th>
                <th>WO# <SortButton columnKey="wo" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>
                  Client WO# <SortButton columnKey="clientWorkOrder" sortConfig={sortConfig} onSort={handleSort} />
                </th>
                <th>Site Name <SortButton columnKey="siteName" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>Scope</th>
                <th className="si-col-desc">Job Description</th>
                <th>Service Date <SortButton columnKey="startDate" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>Technician <SortButton columnKey="techAssign" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>Company</th>
                <th>Completed On</th>
                <th>Invoice</th>
                <th className="si-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="si-empty">Loading completed jobs…</td>
                </tr>
              ) : visibleCompletedRows.length === 0 ? (
                <tr>
                  <td colSpan="12" className="si-empty">
                    {sendInvoicesSearch
                      ? "No completed jobs match your search."
                      : "Nothing to invoice. Jobs marked complete on Home show up here."}
                  </td>
                </tr>
              ) : (
                visibleCompletedRows.map((job) => {
                  const info = rowInvoiceInfo(job);
                  const rowKey = getRowKey(job);
                  return (
                    <tr
                      key={rowKey}
                      className={
                        info.hasInvoice
                          ? info.status === "Draft"
                            ? "si-row--draft"
                            : "si-row--approved"
                          : ""
                      }
                    >
                      <td className="si-col-select">
                        <input
                          type="checkbox"
                          checked={selectedJobIds.includes(rowKey)}
                          onChange={() => toggleJobSelection(rowKey)}
                        />
                      </td>
                      <td className="si-strong">{job.wo}</td>
                      <td>{job.clientWorkOrder || ""}</td>
                      <td>{job.siteName}</td>
                      <td>{job.scope}</td>
                      <td className="si-col-desc">{getShortJobDescription(job.jobDescription)}</td>
                      <td>
                        {job.startDate}
                        {isRecurring(job) && (
                          <div>
                            <Pill tone="purple">Recurring visit</Pill>
                          </div>
                        )}
                      </td>
                      <td>{displayTechAssign(job.techAssign)}</td>
                      <td>{job.companyUse || ""}</td>
                      <td>{formatCompletedAt(job.completedAt) || "—"}</td>
                      <td>
                        {info.hasInvoice ? (
                          <>
                            <Pill tone={statusTone(info.status)}>{info.status}</Pill>
                            {info.latest && (
                              <div className="si-muted si-small">{formatMoney(info.latest.amount)}</div>
                            )}
                          </>
                        ) : (
                          <Pill tone="grey">Not created</Pill>
                        )}
                      </td>
                      <td className="si-col-actions">
                        <div className="si-action-stack">
                          <button
                            type="button"
                            className="si-btn si-btn--blue si-btn--sm"
                            onClick={() => setSelectedInvoiceJob(job)}
                          >
                            Details
                          </button>
                          {info.hasInvoice ? (
                            <>
                              <button
                                type="button"
                                className="si-btn si-btn--green si-btn--sm"
                                onClick={goToApprovedInvoices}
                              >
                                Review Invoice
                              </button>
                              <button
                                type="button"
                                className="si-btn si-btn--outline si-btn--sm"
                                onClick={() => setJobToUnapprove(job)}
                              >
                                Un-Approve Invoice
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="si-btn si-btn--red si-btn--sm"
                              onClick={() => openCreateInvoice(job)}
                            >
                              Create Invoice
                            </button>
                          )}
                          <button
                            type="button"
                            className="si-btn si-btn--teal si-btn--sm"
                            onClick={() => openFinancials(job)}
                          >
                            Financials
                          </button>
                          <button
                            type="button"
                            className="si-btn si-btn--grey si-btn--sm"
                            onClick={() => handleShowVerificationPopup(job)}
                          >
                            Invoice Sent
                          </button>
                          <button
                            type="button"
                            className={`si-btn si-btn--sm ${getTotalFor(job) ? "si-btn--yellow" : "si-btn--outline"}`}
                            onClick={() => openTotalPopup(job)}
                          >
                            {getTotalFor(job) ? "Change Total" : "Add Total"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ================= Sent Invoices ================= */}
      <section className="si-card">
        <div className="si-card-head">
          <h3 className="si-card-title">
            Sent Invoices <span className="si-count">{visibleSentRows.length}</span>
          </h3>
          <button
            type="button"
            className="si-btn si-btn--outline si-btn--sm"
            onClick={() => setShowSentInvoices((prev) => !prev)}
          >
            {showSentInvoices ? "Hide" : "Show"}
          </button>
        </div>

        {showSentInvoices && (
          <div className="si-table-wrap" style={{ maxHeight: "50vh" }}>
            <table className="si-table si-table--sent">
              <thead>
                <tr>
                  <th>WO#</th>
                  <th>Site Name</th>
                  <th>Scope</th>
                  <th className="si-col-desc">Job Description</th>
                  <th>Service Date</th>
                  <th>Technician</th>
                  <th>Company</th>
                  <th>Status</th>
                  <th className="si-col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleSentRows.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="si-empty">
                      {sendInvoicesSearch ? "No sent invoices match your search." : "No sent invoices yet."}
                    </td>
                  </tr>
                ) : (
                  visibleSentRows.map((job) => (
                    <tr key={getRowKey(job)}>
                      <td className="si-strong">{job.wo}</td>
                      <td>{job.siteName}</td>
                      <td>{job.scope}</td>
                      <td className="si-col-desc">{getShortJobDescription(job.jobDescription)}</td>
                      <td>
                        {job.startDate}
                        {isRecurring(job) && (
                          <div>
                            <Pill tone="purple">Recurring visit</Pill>
                          </div>
                        )}
                      </td>
                      <td>{displayTechAssign(job.techAssign)}</td>
                      <td>{job.companyUse || ""}</td>
                      <td>
                        <Pill tone="blue">Invoice Sent</Pill>
                      </td>
                      <td className="si-col-actions">
                        <div className="si-action-stack">
                          <button
                            type="button"
                            className="si-btn si-btn--blue si-btn--sm"
                            onClick={() => setSelectedInvoiceJob(job)}
                          >
                            Details
                          </button>
                          <button
                            type="button"
                            className="si-btn si-btn--grey si-btn--sm"
                            onClick={() => setJobToUnsend(job)}
                          >
                            Move Back to Completed
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ================= Deposits ================= */}
      <section className="si-card">
        <div className="si-card-head">
          <h3 className="si-card-title">Send Invoice Deposits</h3>
          <div className="si-search">
            <input
              type="text"
              value={depositInvoicesSearch}
              onChange={(e) => setDepositInvoicesSearch(e.target.value)}
              placeholder="Search deposit invoices by WO, site name, scope, or description..."
            />
          </div>
        </div>
        <div className="si-table-wrap" style={{ maxHeight: "70vh" }}>
          <table className="si-table si-table--deposit">
            <thead>
              <tr>
                <th>WO# <SortButton columnKey="wo" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>Site Name <SortButton columnKey="siteName" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>Scope</th>
                <th className="si-col-desc">Job Description</th>
                <th>Start Date <SortButton columnKey="startDate" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>End Date</th>
                <th>Technician</th>
                <th>Company</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortData(
                filterJobsByInvoiceSearch(approvedJobs, depositInvoicesSearch),
                sortConfig.key,
                sortConfig.direction
              ).length === 0 ? (
                <tr>
                  <td colSpan="10" className="si-empty">No deposit invoices waiting.</td>
                </tr>
              ) : (
                sortData(
                  filterJobsByInvoiceSearch(approvedJobs, depositInvoicesSearch),
                  sortConfig.key,
                  sortConfig.direction
                ).map((job) => (
                  <tr key={job.id} className={job.approvedInvoice ? "si-row--approved" : ""}>
                    <td className="si-strong">{job.wo}</td>
                    <td>{job.siteName}</td>
                    <td>{job.scope}</td>
                    <td className="si-col-desc">{getShortJobDescription(job.jobDescription)}</td>
                    <td>{job.startDate}</td>
                    <td>{job.endDate}</td>
                    <td>{displayTechAssign(job.techAssign)}</td>
                    <td>{job.companyUse || ""}</td>
                    <td>
                      <Pill tone={statusTone(job.status)}>{job.status}</Pill>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="si-btn si-btn--blue si-btn--sm"
                        onClick={() => setSelectedDepositJob(job)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ================= Monthly ================= */}
      <section className="si-card">
        <div className="si-card-head">
          <h3 className="si-card-title">Monthly Jobs</h3>
          <div className="si-search">
            <input
              type="text"
              value={monthlyInvoicesSearch}
              onChange={(e) => setMonthlyInvoicesSearch(e.target.value)}
              placeholder="Search monthly jobs by site name, scope, or technician..."
            />
          </div>
        </div>
        <div className="si-toolbar">
          <button
            type="button"
            className="si-btn si-btn--outline si-btn--sm"
            onClick={() => setShowClosedAccounts((prev) => !prev)}
          >
            {showClosedAccounts ? "Hide Closed Accounts" : "Show Closed Accounts"}
          </button>
          <button
            type="button"
            className="si-btn si-btn--blue si-btn--sm"
            onClick={() => setShowAddMonthlyJobPopup(true)}
          >
            Add Monthly Jobs to Invoice
          </button>
        </div>
        <div className="si-table-wrap" style={{ maxHeight: "70vh" }}>
          <table className="si-table si-table--monthly">
            <thead>
              <tr>
                <th>Site Name <SortButton columnKey="siteName" sortConfig={sortConfig} onSort={handleSort} /></th>
                <th>Scope</th>
                <th>Technician</th>
                <th>Company</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const list = sortData(
                  filterJobsByInvoiceSearch(
                    monthlyJobs.filter((job) => showClosedAccounts || job.status !== "Closed"),
                    monthlyInvoicesSearch
                  ),
                  sortConfig.key,
                  sortConfig.direction
                );
                if (!list.length) {
                  return (
                    <tr>
                      <td colSpan="7" className="si-empty">No monthly jobs.</td>
                    </tr>
                  );
                }
                return list.map((job) => (
                  <tr key={job.id} className={job.status === "Closed" ? "si-row--closed" : ""}>
                    <td className="si-strong">{job.siteName}</td>
                    <td>{job.scope}</td>
                    <td>{displayTechAssign(job.techAssign)}</td>
                    <td>{job.companyUse || ""}</td>
                    <td>{job.price ? formatMoney(job.price) : ""}</td>
                    <td>
                      <Pill tone={statusTone(job.status)}>{job.status}</Pill>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="si-btn si-btn--blue si-btn--sm"
                        onClick={() => setSelectedMonthlyJob(job)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </section>

      {/* ================= Job Details popup ================= */}
      {detailsJob && (
        <SiPopup
          layer="main"
          width={1000}
          title="Job Details"
          subtitle={[detailsJob.wo && `WO ${detailsJob.wo}`, detailsJob.siteName].filter(Boolean).join(" · ")}
          onClose={() => setSelectedInvoiceJob(null)}
        >
          <div className="si-detail-grid">
            <DetailRow label="Site Name" value={detailsJob.siteName} />
            <DetailRow label="Work Order" value={detailsJob.wo} />
            <DetailRow label="Client Work Order" value={detailsJob.clientWorkOrder || "N/A"} />
            <DetailRow label="Company" value={detailsJob.companyUse || "N/A"} />
            <DetailRow label="Address" value={detailsJob.address} />
            <DetailRow label="City" value={detailsJob.city} />
            <DetailRow label="State" value={detailsJob.state} />
            <DetailRow label="Zip" value={detailsJob.zip} />
            <DetailRow label={isRecurring(detailsJob) ? "Visit Date" : "Start Date"} value={detailsJob.startDate} />
            {!isRecurring(detailsJob) && <DetailRow label="End Date" value={detailsJob.endDate} />}
            <DetailRow label="Service Type" value={detailsJob.serviceType} />
            <DetailRow label="Scope" value={detailsJob.scope} />
            <DetailRow label="Tech Assign" value={displayTechAssign(detailsJob.techAssign)} />
            <DetailRow
              label="Status"
              value={<Pill tone={statusTone(detailsIsSent ? "Invoice Sent" : detailsJob.status)}>{detailsIsSent ? "Invoice Sent" : detailsJob.status}</Pill>}
            />
            {formatCompletedAt(detailsJob.completedAt) && (
              <DetailRow label="Completed On" value={formatCompletedAt(detailsJob.completedAt)} />
            )}
            {renderPriceLines(detailsJob)}
          </div>

          <div className="si-section">
            <h4 className="si-section-title">Job Description</h4>
            <div className="si-desc-box">{parseFormattedJobDescription(detailsJob.jobDescription)}</div>
          </div>

          {/* Invoice status for this job / visit */}
          <div className="si-section si-invoice-box">
            <h4 className="si-section-title">Invoice</h4>
            {detailsInvoiceInfo?.invoices?.length ? (
              <div className="si-invoice-list">
                {detailsInvoiceInfo.invoices.map((inv) => (
                  <div key={inv.id} className="si-invoice-line">
                    <span className="si-strong">#{inv.invoiceNumber || inv.wo || inv.id}</span>
                    <Pill tone={statusTone(invoiceStatusLabel(inv))}>{invoiceStatusLabel(inv)}</Pill>
                    <span>{formatMoney(inv.amount)}</span>
                    <span className="si-muted">Service date {inv.dateOfService || inv.serviceDate || "—"}</span>
                  </div>
                ))}
                <div className="si-btn-row" style={{ marginTop: 8 }}>
                  <button type="button" className="si-btn si-btn--green" onClick={goToApprovedInvoices}>
                    Review in Approved Invoices
                  </button>
                  <button
                    type="button"
                    className="si-btn si-btn--outline"
                    onClick={() => setJobToUnapprove(detailsJob)}
                  >
                    Un-Approve Invoice
                  </button>
                </div>
              </div>
            ) : (
              <div className="si-btn-row">
                <span className="si-muted">No invoice has been created for this {isRecurring(detailsJob) ? "visit" : "job"} yet.</span>
                {!detailsIsSent && (
                  <button type="button" className="si-btn si-btn--red" onClick={() => openCreateInvoice(detailsJob)}>
                    Create Invoice
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="si-section">
            <h4 className="si-section-title">Notes</h4>
            {renderNotes(detailsJob.notes)}
          </div>

          {renderUploadedFiles(detailsJob)}

          <div className="si-btn-row">
            <button type="button" className="si-btn si-btn--grey" onClick={() => handleEdit(detailsJob, "job")}>
              Edit
            </button>
            <button type="button" className="si-btn si-btn--blue" onClick={() => handleShowNotePopup(detailsJob)}>
              Add Note
            </button>
            <button type="button" className="si-btn si-btn--teal" onClick={() => openFinancials(detailsJob)}>
              Financials
            </button>
            {detailsIsSent ? (
              <button type="button" className="si-btn si-btn--green" onClick={() => setJobToUnsend(detailsJob)}>
                Move Back to Completed
              </button>
            ) : (
              <button
                type="button"
                className="si-btn si-btn--green"
                onClick={() => handleShowVerificationPopup(detailsJob)}
              >
                Invoice Sent
              </button>
            )}
          </div>

          <div className="si-btn-row">
            <button type="button" className="si-btn si-btn--grey" onClick={() => addSupervisorNote(detailsJob)}>
              Add Supervisor Note
            </button>
            <button type="button" className="si-btn si-btn--grey" onClick={() => setShowHistoryPopup(true)}>
              History
            </button>
            <button
              type="button"
              className="si-btn si-btn--teal"
              onClick={() => openContinuePopup(detailsJob)}
              title="Create a new active job that stems off this work order"
            >
              Continue
            </button>
            {detailsJob.isProject && (
              <button type="button" className="si-btn si-btn--red" onClick={() => markProjectFullyCompleted(detailsJob)}>
                Project Completed
              </button>
            )}
            <button
              type="button"
              className="si-btn si-btn--blue"
              onClick={() => {
                setSelectedJob(detailsJob);
                setShowReceiptPopup(true);
              }}
            >
              Add Receipt
            </button>
            <button
              type="button"
              className="si-btn si-btn--blue"
              onClick={() => {
                setSelectedJob(detailsJob);
                setShowMaterialsPopup(true);
              }}
            >
              Add / Edit Materials
            </button>
            <button
              type="button"
              className="si-btn si-btn--yellow"
              onClick={() =>
                navigate(`/JobChecklist/${detailsJob.id}`, {
                  state: {
                    returnTo: `${window.location.pathname}${window.location.search || ""}`,
                    reopenJobId: detailsJob.id,
                    reopenOccurrenceDate: detailsJob.occurrenceDate || detailsJob.startDate || "",
                  },
                })
              }
            >
              Checklist
            </button>
          </div>

          <div className="si-btn-row">
            <button
              type="button"
              className={`si-btn ${getTotalFor(detailsJob) ? "si-btn--yellow" : "si-btn--blue"}`}
              onClick={() => openTotalPopup(detailsJob)}
            >
              {getTotalFor(detailsJob) ? "Change Total" : "Add Total"}
            </button>
            <button type="button" className="si-btn si-btn--black" onClick={() => handleDeleteRequest(detailsJob)}>
              Delete
            </button>
            <button type="button" className="si-btn si-btn--red" onClick={() => setSelectedInvoiceJob(null)}>
              Close
            </button>
          </div>
        </SiPopup>
      )}

      {/* Complete Job & Create Invoice form (Priority). Saves to Approved
          Invoices as Approved or Draft - emailing happens on Approved Invoices. */}
      {invoiceModalJob && (
        <JobCompletionInvoiceModal
          job={invoiceModalJob}
          amount={getTotalFor(invoiceModalJob)}
          onClose={() => setInvoiceModalJob(null)}
          onCompleted={(invoice) => handleInvoiceCreated(invoiceModalJob, invoice)}
        />
      )}

      <ContinueJobPopup
        open={Boolean(continueSourceJob)}
        sourceJob={continueSourceJob}
        nextWO={continueNextWO}
        loading={continueLoading}
        saving={continueSaving}
        error={continueError}
        onConfirm={confirmContinue}
        onCancel={closeContinuePopup}
      />

      {showHistoryPopup && selectedInvoiceJob && (
        <JobHistoryPopup job={selectedInvoiceJob} jobs={jobs} onClose={() => setShowHistoryPopup(false)} />
      )}

      {/* ================= Deposit details ================= */}
      {selectedDepositJob && !isEditing && (
        <SiPopup
          layer="main"
          width={900}
          title="Deposit Job Details"
          subtitle={[selectedDepositJob.wo && `WO ${selectedDepositJob.wo}`, selectedDepositJob.siteName].filter(Boolean).join(" · ")}
          onClose={() => setSelectedDepositJob(null)}
        >
          <div className="si-detail-grid">
            <DetailRow label="Site Name" value={selectedDepositJob.siteName} />
            <DetailRow label="Work Order" value={selectedDepositJob.wo} />
            <DetailRow label="Client Work Order" value={selectedDepositJob.clientWorkOrder || "N/A"} />
            <DetailRow label="Company" value={selectedDepositJob.companyUse || "N/A"} />
            <DetailRow label="Address" value={selectedDepositJob.address} />
            <DetailRow label="City" value={selectedDepositJob.city} />
            <DetailRow label="State" value={selectedDepositJob.state} />
            <DetailRow label="Zip" value={selectedDepositJob.zip} />
            <DetailRow label="Start Date" value={selectedDepositJob.startDate} />
            <DetailRow label="End Date" value={selectedDepositJob.endDate} />
            <DetailRow label="Service Type" value={selectedDepositJob.serviceType} />
            <DetailRow label="Scope" value={selectedDepositJob.scope} />
            <DetailRow label="Tech Assign" value={displayTechAssign(selectedDepositJob.techAssign)} />
            <DetailRow label="Status" value={selectedDepositJob.status} />
            {renderPriceLines(selectedDepositJob)}
          </div>
          <div className="si-section">
            <h4 className="si-section-title">Job Description</h4>
            <div className="si-desc-box">{parseFormattedJobDescription(selectedDepositJob.jobDescription)}</div>
          </div>
          <div className="si-section">
            <h4 className="si-section-title">Notes</h4>
            {renderNotes(selectedDepositJob.notes)}
          </div>
          {renderUploadedFiles(selectedDepositJob)}
          <div className="si-btn-row">
            <button type="button" className="si-btn si-btn--grey" onClick={() => handleEdit(selectedDepositJob, "job")}>
              Edit
            </button>
            <button type="button" className="si-btn si-btn--grey" onClick={() => handleShowNotePopup(selectedDepositJob)}>
              Add Note
            </button>
            <button type="button" className="si-btn si-btn--teal" onClick={() => openFinancials(selectedDepositJob)}>
              Financials
            </button>
            <button
              type="button"
              className="si-btn si-btn--teal"
              onClick={() => handleShowVerificationPopup(selectedDepositJob, "deposit")}
            >
              Deposit Sent
            </button>
            <button type="button" className="si-btn si-btn--purple" onClick={() => setShowDepositPricePopup(true)}>
              Add Deposit Price
            </button>
          </div>
          <div className="si-btn-row">
            <button type="button" className="si-btn si-btn--black" onClick={() => handleDeleteRequest(selectedDepositJob)}>
              Delete
            </button>
            <button type="button" className="si-btn si-btn--red" onClick={() => setSelectedDepositJob(null)}>
              Close
            </button>
          </div>
        </SiPopup>
      )}

      {/* ================= Edit job / monthly job ================= */}
      {isEditing && editedJob && (
        <SiPopup
          width={900}
          title={editingMode === "monthly" ? "Edit Monthly Job" : "Edit Job"}
          onClose={handleCancel}
        >
          {editingMode !== "monthly" && isRecurring(editedJob) && editedJob.__occurrenceRow && (
            <p className="si-callout">
              This is one visit of a recurring job. When you save you will be asked whether the
              change applies to this visit only or to this and future visits.
            </p>
          )}
          <div className="si-form">
            <div className="si-field">
              <label>Site Name</label>
              <select
                value={editedJob.siteName || ""}
                onChange={(e) => setEditedJob((prev) => fillJobFromProperty(e.target.value, prev))}
              >
                <option value="">Select Site</option>
                {withCurrentValue(siteOptions.map((s) => s.label), editedJob.siteName).map((label, i) => (
                  <option key={`${label}_${i}`} value={label}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="si-field">
              <label>Address</label>
              <input type="text" value={editedJob.address || ""} readOnly />
            </div>
            <div className="si-field">
              <label>City</label>
              <input type="text" value={editedJob.city || ""} readOnly />
            </div>
            <div className="si-field">
              <label>State</label>
              <input type="text" value={editedJob.state || ""} readOnly />
            </div>
            <div className="si-field">
              <label>Zip</label>
              <input type="text" value={editedJob.zip || ""} readOnly />
            </div>
            {editingMode !== "monthly" && (
              <div className="si-field">
                <label>Client Work Order</label>
                <input
                  type="text"
                  value={editedJob.clientWorkOrder || ""}
                  onChange={(e) => setEditedJob((prev) => ({ ...prev, clientWorkOrder: e.target.value }))}
                />
              </div>
            )}
            <div className="si-field">
              <label>Scope</label>
              <input
                type="text"
                value={editedJob.scope || ""}
                onChange={(e) => setEditedJob((prev) => ({ ...prev, scope: e.target.value }))}
              />
            </div>
            {editingMode !== "monthly" && (
              <div className="si-field">
                <label>Service Type</label>
                <select
                  value={editedJob.serviceType || ""}
                  onChange={(e) => setEditedJob((prev) => ({ ...prev, serviceType: e.target.value }))}
                >
                  <option value="">Select Service Type</option>
                  {withCurrentValue(serviceTypes, editedJob.serviceType).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="si-field si-field--full">
              <label>Description</label>
              <textarea
                rows="8"
                value={editedJob.jobDescription || editedJob.descriptionField || ""}
                onChange={(e) =>
                  setEditedJob((prev) => ({
                    ...prev,
                    jobDescription: e.target.value,
                    descriptionField: e.target.value,
                  }))
                }
              />
            </div>

            {editingMode !== "monthly" ? (
              <>
                <div className="si-field">
                  <label>{isRecurring(editedJob) && editedJob.__occurrenceRow ? "Visit Date" : "Start Date"}</label>
                  <input
                    type="date"
                    value={editedJob.startDate || ""}
                    onChange={(e) => setEditedJob((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                </div>
                {!isRecurring(editedJob) && (
                  <div className="si-field">
                    <label>End Date</label>
                    <input
                      type="date"
                      value={editedJob.endDate || ""}
                      onChange={(e) => setEditedJob((prev) => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="si-field">
                  <label>Frequency</label>
                  <input
                    type="text"
                    value={editedJob.frequency || ""}
                    onChange={(e) => setEditedJob((prev) => ({ ...prev, frequency: e.target.value }))}
                  />
                </div>
                <div className="si-field">
                  <label>Price</label>
                  <input
                    type="text"
                    value={editedJob.price || ""}
                    onChange={(e) => setEditedJob((prev) => ({ ...prev, price: e.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="si-field si-field--full">
              <label>Tech Assign</label>
              {normalizeTechAssign(editedJob.techAssign, true).map((tech, index) => (
                <div key={index} className="si-inline">
                  <select
                    value={tech}
                    onChange={(e) => {
                      const updated = [...normalizeTechAssign(editedJob.techAssign, true)];
                      updated[index] = e.target.value;
                      setEditedJob((prev) => ({ ...prev, techAssign: updated }));
                    }}
                  >
                    <option value="">Select Technician</option>
                    {withCurrentValue(
                      [
                        ...dropdownUsers.map((user) => `${user.firstName || ""} ${user.lastName || ""}`.trim()),
                        ...contractorsList.map((c) => c.companyName).filter(Boolean),
                      ],
                      tech
                    ).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="si-btn si-btn--red si-btn--sm"
                    onClick={() => {
                      const updated = normalizeTechAssign(editedJob.techAssign, true).filter((_, i) => i !== index);
                      setEditedJob((prev) => ({ ...prev, techAssign: updated }));
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div>
                <button
                  type="button"
                  className="si-btn si-btn--blue si-btn--sm"
                  onClick={() =>
                    setEditedJob((prev) => ({
                      ...prev,
                      techAssign: [...normalizeTechAssign(prev.techAssign, true), ""],
                    }))
                  }
                >
                  Add Technician
                </button>
              </div>
            </div>

            <div className="si-field">
              <label>Company</label>
              <select
                value={editedJob.companyUse || ""}
                onChange={(e) => setEditedJob((prev) => ({ ...prev, companyUse: e.target.value }))}
              >
                <option value="">Select Company</option>
                {withCurrentValue(companyOptions, editedJob.companyUse).map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </div>

            {!(editingMode !== "monthly" && isRecurring(editedJob) && editedJob.__occurrenceRow) && (
              <div className="si-field">
                <label>Status</label>
                <select
                  value={editedJob.status || ""}
                  onChange={(e) => setEditedJob((prev) => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">Select Status</option>
                  {withCurrentValue(
                    editingMode === "monthly" ? monthlyStatusOptions : statusOptions,
                    editedJob.status
                  ).map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={handleCancel} disabled={savingEdit}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={handleSave} disabled={savingEdit}>
              {savingEdit ? "Saving…" : "Save"}
            </button>
          </div>
        </SiPopup>
      )}

      {/* ================= Monthly details ================= */}
      {selectedMonthlyJob && !isEditing && (
        <SiPopup
          layer="main"
          width={760}
          title="Monthly Job Details"
          subtitle={selectedMonthlyJob.siteName}
          onClose={() => setSelectedMonthlyJob(null)}
        >
          <div className="si-detail-grid">
            <DetailRow label="Site Name" value={selectedMonthlyJob.siteName} />
            <DetailRow label="Scope" value={selectedMonthlyJob.scope} />
            <DetailRow label="Company" value={selectedMonthlyJob.companyUse || "N/A"} />
            <DetailRow label="Address" value={selectedMonthlyJob.address} />
            <DetailRow label="City" value={selectedMonthlyJob.city} />
            <DetailRow label="Frequency" value={selectedMonthlyJob.frequency} />
            <DetailRow label="Price" value={selectedMonthlyJob.price ? formatMoney(selectedMonthlyJob.price) : ""} />
            <DetailRow label="Technician" value={displayTechAssign(selectedMonthlyJob.techAssign)} />
            <DetailRow label="Status" value={<Pill tone={statusTone(selectedMonthlyJob.status)}>{selectedMonthlyJob.status}</Pill>} />
          </div>
          <div className="si-section">
            <h4 className="si-section-title">Monthly Invoice Notes</h4>
            {renderNotes(selectedMonthlyJob.notes)}
          </div>
          <div className="si-btn-row">
            <button type="button" className="si-btn si-btn--blue" onClick={() => handleShowNotePopup(selectedMonthlyJob, "monthly")}>
              Add Note
            </button>
            <button type="button" className="si-btn si-btn--grey" onClick={() => handleEdit(selectedMonthlyJob, "monthly")}>
              Edit
            </button>
            <button type="button" className="si-btn si-btn--purple" onClick={() => setShowMonthlyInvoicePopup(true)}>
              Monthly Invoice
            </button>
            <button type="button" className="si-btn si-btn--black" onClick={() => handleContractClosed(selectedMonthlyJob)}>
              Contract Closed
            </button>
            <button type="button" className="si-btn si-btn--red" onClick={() => setSelectedMonthlyJob(null)}>
              Close
            </button>
          </div>
        </SiPopup>
      )}

      {showMonthlyInvoicePopup && (
        <SiPopup title="Monthly Invoice" onClose={() => setShowMonthlyInvoicePopup(false)}>
          <div className="si-form si-form--single">
            <div className="si-field">
              <label>Invoice Month</label>
              <select name="invoiceMonth" value={invoiceDetails.invoiceMonth} onChange={handleMonthlyInvoiceInputChange}>
                <option value="">Select Month</option>
                {years.map((year) =>
                  months.map((month) => (
                    <option key={`${month} ${year}`} value={`${month} ${year}`}>
                      {`${month} ${year}`}
                    </option>
                  ))
                )}
              </select>
            </div>
            <div className="si-field">
              <label>Date Invoice Sent</label>
              <input type="date" name="dateInvoiceSent" value={invoiceDetails.dateInvoiceSent} onChange={handleMonthlyInvoiceInputChange} />
            </div>
            <div className="si-field">
              <label>Payment Number</label>
              <input type="text" name="paymentNumber" value={invoiceDetails.paymentNumber} onChange={handleMonthlyInvoiceInputChange} />
            </div>
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowMonthlyInvoicePopup(false)}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--blue" onClick={handleMonthlyInvoiceSubmit}>
              Submit
            </button>
          </div>
        </SiPopup>
      )}

      {/* ================= Confirmations ================= */}
      {showBulkConfirm && (
        <SiPopup title="Please confirm" onClose={bulkWorking ? undefined : handleCancelBulkAction} width={480}>
          <p>
            {bulkActionType === "delete"
              ? `Delete ${selectedJobIds.length} job(s)? Recurring visits are skipped - delete those from their Details.`
              : `Mark ${selectedJobIds.length} job(s) as "${bulkStatusTarget}"?`}
          </p>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={handleCancelBulkAction} disabled={bulkWorking}>
              No
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={handleConfirmBulkAction} disabled={bulkWorking}>
              {bulkWorking ? "Working…" : "Yes"}
            </button>
          </div>
        </SiPopup>
      )}

      {jobToUnapprove && (
        <SiPopup title="Un-approve this invoice?" onClose={() => setJobToUnapprove(null)} width={500}>
          <p>
            This removes the draft/approved invoice for{" "}
            <strong>{jobToUnapprove.wo || jobToUnapprove.siteName}</strong>
            {isRecurring(jobToUnapprove) ? ` (visit ${jobToUnapprove.startDate})` : ""} from Approved
            Invoices so it can be created again. The job stays completed and is not deleted.
          </p>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setJobToUnapprove(null)}>
              No
            </button>
            <button type="button" className="si-btn si-btn--red" onClick={unapproveInvoice}>
              Yes, Un-Approve
            </button>
          </div>
        </SiPopup>
      )}

      {jobToUnsend && (
        <SiPopup title="Move back to Send Invoices?" onClose={() => setJobToUnsend(null)} width={500}>
          <p>
            This moves <strong>{jobToUnsend.wo || jobToUnsend.siteName}</strong>
            {isRecurring(jobToUnsend) ? ` (visit ${jobToUnsend.startDate})` : ""} from Invoice Sent back
            to Completed so it can be re-invoiced.
          </p>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setJobToUnsend(null)}>
              No
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={moveInvoiceSentBackToCompleted}>
              Yes
            </button>
          </div>
        </SiPopup>
      )}

      {showVerificationPopup && (
        <SiPopup
          title={showVerificationPopup === "deposit" ? "Deposit invoice sent?" : "Mark invoice as sent?"}
          onClose={handleCloseVerificationPopup}
          width={500}
        >
          {showVerificationPopup === "deposit" ? (
            <p>Are you sure you sent the deposit invoice?</p>
          ) : (
            <>
              <p>
                Use this when the invoice for <strong>{verificationRow?.wo || verificationRow?.siteName}</strong>
                {verificationRow && isRecurring(verificationRow) ? ` (visit ${verificationRow.startDate})` : ""} went out
                some other way. To email it, review it on Approved Invoices instead.
              </p>
              {verificationInvoice &&
                !["Sent", "Paid"].includes(invoiceStatusLabel(verificationInvoice)) && (
                  <label className="si-check">
                    <input
                      type="checkbox"
                      checked={alsoMarkInvoiceSent}
                      onChange={(e) => setAlsoMarkInvoiceSent(e.target.checked)}
                    />
                    Also mark its invoice in Approved Invoices as sent
                  </label>
                )}
            </>
          )}
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={handleCloseVerificationPopup}>
              No
            </button>
            <button
              type="button"
              className="si-btn si-btn--green"
              onClick={() =>
                showVerificationPopup === "deposit"
                  ? handleDepositSent(selectedJob?.id)
                  : handleInvoiceSent(selectedJob)
              }
            >
              Yes
            </button>
          </div>
        </SiPopup>
      )}

      {showDeleteVerificationPopup && selectedJob && (
        <SiPopup title="Delete this job?" onClose={() => setShowDeleteVerificationPopup(false)} width={480}>
          <p>
            <strong>{selectedJob.wo || selectedJob.siteName}</strong> will be permanently deleted.
          </p>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowDeleteVerificationPopup(false)}>
              No
            </button>
            <button type="button" className="si-btn si-btn--red" onClick={() => handleDeleteJob(selectedJob)}>
              Yes, Delete
            </button>
          </div>
        </SiPopup>
      )}

      {showNotePopup && (
        <SiPopup title="Add Note" onClose={uploading ? undefined : closeNotePopup} width={520}>
          <div className="si-form si-form--single">
            <div className="si-field">
              <label>Note</label>
              <textarea
                rows="4"
                placeholder="Enter your note here..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </div>
            <div className="si-field">
              <label>Attachments</label>
              <input type="file" accept="image/*,.pdf" onChange={handleFileChange} multiple />
            </div>
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={closeNotePopup} disabled={uploading}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={saveNote} disabled={uploading}>
              {uploading ? <LoadingSpinner label="Saving note..." /> : "Save"}
            </button>
          </div>
        </SiPopup>
      )}

      {showDepositPricePopup && (
        <SiPopup title="Add Deposit Price" onClose={() => setShowDepositPricePopup(false)} width={420}>
          <div className="si-form si-form--single">
            <div className="si-field">
              <label>Price ($)</label>
              <input type="number" value={depositPrice} onChange={(e) => setDepositPrice(e.target.value)} />
            </div>
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowDepositPricePopup(false)}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={handleSaveDepositPrice}>
              Save
            </button>
          </div>
        </SiPopup>
      )}

      {showTotalPopup && selectedJob && (
        <SiPopup
          title={getTotalFor(selectedJob) ? "Change Total" : "Add Total"}
          subtitle={[selectedJob.wo && `WO ${selectedJob.wo}`, selectedJob.siteName].filter(Boolean).join(" · ")}
          onClose={() => setShowTotalPopup(false)}
          width={420}
        >
          <div className="si-form si-form--single">
            <div className="si-field">
              <label>Total Amount ($)</label>
              <input
                type="number"
                value={totalInput}
                onChange={(e) => setTotalInput(e.target.value)}
                placeholder="Enter amount"
              />
            </div>
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowTotalPopup(false)}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={saveTotal}>
              Save
            </button>
          </div>
        </SiPopup>
      )}

      {showMaterialsPopup && selectedJob && (
        <SiPopup
          title="Add / Edit Materials"
          subtitle="Check Purchased when the material has been bought. Estimated cost = Qty × Cost Per."
          onClose={() => setShowMaterialsPopup(false)}
          width={1050}
        >
          <div style={{ display: "grid", gap: 12 }}>
            {materialsDraft.map((material, index) => (
              <div key={index} className={`si-material ${material.purchased ? "si-material--bought" : ""}`}>
                <div className="si-material-head">
                  <label className="si-check">
                    <input
                      type="checkbox"
                      checked={Boolean(material.purchased)}
                      onChange={(event) =>
                        updateMaterialDraft(index, {
                          purchased: event.target.checked,
                          purchasedAt: event.target.checked ? new Date().toISOString() : "",
                          purchasedBy: event.target.checked ? userFullName || "User" : "",
                        })
                      }
                    />
                    Purchased
                  </label>
                  <button type="button" className="si-btn si-btn--red si-btn--sm" onClick={() => removeMaterialDraft(index)}>
                    Remove
                  </button>
                </div>
                <div className="si-form">
                  <div className="si-field">
                    <label>Product</label>
                    <input
                      type="text"
                      value={material.name || ""}
                      onChange={(e) => updateMaterialDraft(index, { name: e.target.value })}
                    />
                  </div>
                  <div className="si-field">
                    <label>Qty</label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={material.qty ?? ""}
                      onChange={(e) =>
                        updateMaterialDraft(index, { qty: e.target.value === "" ? "" : Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="si-field">
                    <label>Cost Per</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={material.costPer ?? ""}
                      onChange={(e) =>
                        updateMaterialDraft(index, { costPer: e.target.value === "" ? "" : Number(e.target.value) })
                      }
                    />
                  </div>
                  <div className="si-field">
                    <label>Estimated Material Cost</label>
                    <input type="text" readOnly value={formatMoney(getEstimatedMaterialCost(material))} />
                  </div>
                  <div className="si-field">
                    <label>Purchase Location</label>
                    <input
                      type="text"
                      value={material.purchaseLocation || ""}
                      onChange={(e) => updateMaterialDraft(index, { purchaseLocation: e.target.value })}
                    />
                  </div>
                  <div className="si-field si-field--full">
                    <label>Material Notes</label>
                    <textarea
                      rows="3"
                      value={material.notes || ""}
                      onChange={(e) => updateMaterialDraft(index, { notes: e.target.value })}
                    />
                  </div>
                  <div className="si-field si-field--full">
                    <label>Photos / Files</label>
                    {Array.isArray(material.files) && material.files.length > 0 && (
                      <div className="si-files">
                        {material.files.map((file, fileIndex) => (
                          <div key={fileIndex} className="si-file-chip">
                            <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                              {isImageUrl(file.name || file.url) ? (
                                <img src={file.url} alt={file.name} className="si-thumb si-thumb--sm" />
                              ) : (
                                <span className="si-file-name">{file.name}</span>
                              )}
                            </a>
                            <button
                              type="button"
                              className="si-file-x"
                              onClick={() => removeMaterialFile(index, fileIndex)}
                              title="Remove file"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={(event) => uploadMaterialFiles(index, event.target.files)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="si-btn-row">
            <button type="button" className="si-btn si-btn--blue" onClick={addMaterial}>
              + Add Material
            </button>
          </div>
          <div className="si-total-box">
            Total Estimated Material Cost:{" "}
            {formatMoney(materialsDraft.reduce((sum, m) => sum + getEstimatedMaterialCost(m), 0))}
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowMaterialsPopup(false)}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={saveMaterials}>
              Save Materials
            </button>
          </div>
        </SiPopup>
      )}

      {showReceiptPopup && selectedJob && (
        <SiPopup title="Add Receipt" onClose={() => setShowReceiptPopup(false)} width={480}>
          <div className="si-form si-form--single">
            <div className="si-field">
              <label>Receipt Amount</label>
              <input
                type="number"
                value={receiptAmount}
                onChange={(e) => setReceiptAmount(e.target.value)}
                placeholder="Enter receipt amount"
              />
            </div>
            <div className="si-field">
              <label>Receipt Note</label>
              <input
                type="text"
                value={receiptNote}
                onChange={(e) => setReceiptNote(e.target.value)}
                placeholder="Example: supply store - bait stations"
              />
            </div>
            <div className="si-field">
              <label>Upload Receipt Image</label>
              <input type="file" accept="image/*,.pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
              {receiptFile && <span className="si-muted si-small">Selected: {receiptFile.name}</span>}
            </div>
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowReceiptPopup(false)}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--green" onClick={saveReceipt} disabled={savingReceipt}>
              {savingReceipt ? "Saving..." : "Save Receipt"}
            </button>
          </div>
        </SiPopup>
      )}

      {showAddMonthlyJobPopup && (
        <SiPopup layer="main" title="Add Monthly Job" onClose={() => setShowAddMonthlyJobPopup(false)} width={760}>
          <div className="si-form">
            <div className="si-field">
              <label>Site Name</label>
              <select name="siteName" value={newMonthlyJob.siteName} onChange={handlePropertyChange}>
                <option value="">Select Site Name</option>
                {sortedPropertiesList.map((property) => (
                  <option key={property.id} value={property.propertyName}>
                    {property.propertyName}
                  </option>
                ))}
              </select>
            </div>
            <div className="si-field">
              <label>Management (if applicable)</label>
              <input type="text" name="management" value={newMonthlyJob.management} readOnly />
            </div>
            <div className="si-field">
              <label>Address</label>
              <input type="text" name="address" value={newMonthlyJob.address} readOnly />
            </div>
            <div className="si-field">
              <label>City</label>
              <input type="text" name="city" value={newMonthlyJob.city} readOnly />
            </div>
            <div className="si-field">
              <label>State</label>
              <input type="text" name="state" value={newMonthlyJob.state} readOnly />
            </div>
            <div className="si-field">
              <label>Zip</label>
              <input type="text" name="zip" value={newMonthlyJob.zip} readOnly />
            </div>
            <div className="si-field">
              <label>Work Order #</label>
              <input type="text" name="wo" value={newMonthlyJob.wo} onChange={handleMonthlyJobInputChange} />
            </div>
            <div className="si-field">
              <label>Scope</label>
              <input type="text" name="scope" value={newMonthlyJob.scope} onChange={handleMonthlyJobInputChange} />
            </div>
            <div className="si-field">
              <label>Assign Tech</label>
              <select name="techAssign" value={newMonthlyJob.techAssign} onChange={handleMonthlyJobInputChange}>
                <option value="">Select Technician</option>
                {dropdownUsers.map((user) => (
                  <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                    {`${user.firstName} ${user.lastName}`}
                  </option>
                ))}
                {contractorsList.map((contractor) => (
                  <option key={contractor.id} value={contractor.companyName}>
                    {contractor.companyName}
                  </option>
                ))}
              </select>
            </div>
            <div className="si-field">
              <label>Company</label>
              <select name="companyUse" value={newMonthlyJob.companyUse} onChange={handleMonthlyJobInputChange}>
                {companyOptions.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>
            </div>
            <div className="si-field">
              <label>Price</label>
              <input type="text" name="price" value={newMonthlyJob.price} onChange={handleMonthlyJobInputChange} />
            </div>
            <div className="si-field">
              <label>Frequency</label>
              <select name="frequency" value={newMonthlyJob.frequency} onChange={handleMonthlyJobInputChange}>
                <option value="">Select Frequency</option>
                <option value="Monthly">Monthly</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-Weekly">Bi-Weekly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            </div>
            <div className="si-field">
              <label>Status</label>
              <input type="text" name="status" value={newMonthlyJob.status} readOnly />
            </div>
          </div>
          <div className="si-btn-row si-btn-row--end">
            <button type="button" className="si-btn si-btn--outline" onClick={() => setShowAddMonthlyJobPopup(false)}>
              Cancel
            </button>
            <button type="button" className="si-btn si-btn--blue" onClick={handleAddMonthlyJob}>
              Add Monthly Job
            </button>
          </div>
        </SiPopup>
      )}
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="si-detail">
    <span className="si-detail-label">{label}</span>
    <span className="si-detail-value">{value === undefined || value === null || value === "" ? "—" : value}</span>
  </div>
);

export default SendInvoices;
